

import { API } from './api'
import { DateField, DateTimeField, toIsoDay } from './DatePicker'
import { useEffect, useMemo, useState } from 'react'
import SeasonalOrdersSettings from './SeasonalOrdersSettings'
import PrintSettingsAdmin from './PrintSettingsAdmin'
import { PrintHost, PrintPreviewFrame } from './PrintHost'
import EshopCategories, { adminRequest, type ShopCategory } from './EshopCategories'
import CustomersAdmin from './CustomersAdmin'
import StatsAdmin from './StatsAdmin'
import InvoicesAdmin from './InvoicesAdmin'
import ShopCategoryPicker from './ShopCategoryPicker'
import ProductsAdmin, { presetProductFilters } from './ProductsAdmin'
import DashboardAdmin, { type DashboardTarget } from './DashboardAdmin'
import DeliveryRatesAdmin from './DeliveryRatesAdmin'
import SiteEditor from './SiteEditor'
import StoresAdmin from './StoresAdmin'
import StockAdmin, { openPrint, loadCatalog, type StockView, type Product as StockProduct, type Category as StockCategory } from './StockAdmin'
import NewOrderAdmin from './NewOrderAdmin'
import PriceTagsAdmin from './PriceTagsAdmin'
import SalesAdmin, { type SalesView } from './SalesAdmin'
import MaintenanceAdmin, { MAINTENANCE_PAGE, MaintenanceBadge } from './MaintenanceAdmin'
import OrderDelivery, { DELIVERY_LABEL, deliveryShort } from './OrderDelivery'
import type { OpeningHours } from './OpeningHoursEditor'
import { ORDER_STATUSES, SCHEDULE_STATUSES, dayEndIso, dayStartIso, friendlyDateTime, orderStatus, orderTerm, paymentTone, scheduleStatus } from './orderLabels'
import { useDragSort } from './dragSort'
import { ALLERGENS, UNIT_OPTIONS, computeB2bPriceCents, formatAllergens, formatShelfLife, inputToGrams, inputToMl, measureKindOf, normalizeUnit, parseAllergens, parseShelfLife, preferredVolumeUnit, preferredWeightUnit, shelfUnitWord, volumeToInput, weightToInput, type B2bPricingMode, type MeasureKind, type ShelfUnit, type VolumeUnit, type WeightUnit } from './productFields'
import FiskalGroupsDialog, { fiskalGroupLabel, type FiskalGroup, type FiskalGroupsInfo } from './FiskalGroups'
import './App.css'
import { filterActiveStockProducts } from './stock-filter'



/** Termín: „ut 23. 12. 2026“ alebo s časom „ut 23. 12. 2026 o 14:30“ (polnoc = iba deň). */
function termLabel(value: string | Date) {
  const d = new Date(value)
  const parts = Object.fromEntries(new Intl.DateTimeFormat('sk-SK', { timeZone: 'Europe/Bratislava', weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d).map(p => [p.type, p.value]))
  const time = `${parts.hour}:${parts.minute}`
  return `${parts.weekday} ${parts.day}. ${parts.month}. ${parts.year}${time !== '00:00' ? ` o ${time}` : ''}`
}
/** Polnoc daného dňa v Bratislave (termín bez času). */
export function bratislavaMidnightIso(day: string) {
  const guess = new Date(`${day}T00:00:00Z`)
  const local = new Date(guess.toLocaleString('en-US', { timeZone: 'Europe/Bratislava' }))
  const utc = new Date(guess.toLocaleString('en-US', { timeZone: 'UTC' }))
  return new Date(guess.getTime() - (local.getTime() - utc.getTime())).toISOString()
}
const todayDay = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bratislava' }).format(new Date())
export const termInputValid = (value: string) => /^\d{4}-\d{2}-\d{2}T\d\d:\d\d$/.test(value) || (/^\d{4}-\d{2}-\d{2}T?$/.test(value) && value.slice(0, 10) > todayDay())
export const termInputIso = (value: string) => /T\d\d:\d\d$/.test(value) ? new Date(value).toISOString() : bratislavaMidnightIso(value.slice(0, 10))

type Order = {

  id: string

  orderNumber: string

  /** Objednávka zo sezónneho okna e-shopu (Vianočná objednávka). */
  seasonalRuleId?: string | null

  customerName: string

  status: string

  totalCents: number
  pickupAt?: string | null
  paymentStatus?: string
  scheduleStatus?: string | null
  requestedFulfillmentAt?: string | null
  proposedFulfillmentAt?: string | null
  confirmedFulfillmentAt?: string | null
  fiskalproOrderId?: string | null
  fiskalproStatus?: string | null
  customerId?: string | null
  paymentMethod?: string
  paidAt?: string | null
  paymentTransId?: string | null
  refundedCents?: number
  invoices?: Array<{ id: string; number: string; kind: string; totalCents: number }>
  discountPercent?: number
  discountCents?: number
  companyName?: string | null
  companyIco?: string | null
  companyDic?: string | null
  companyIcDph?: string | null
  billingAddress?: string | null

  deliveryMethod?: string
  deliveryPriceCents?: number
  deliveryPricePending?: boolean
  deliveryPostalCode?: string | null

  createdAt?: string

  store?: { name: string }
  storeId?: string

  items?: Array<{
    id: string
    quantity: number | string
    unitPriceCents: number
    lineTotalCents: number
    variantName?: string | null
    vatRate?: number | null
    vatOnSite?: boolean
    product: { name: string; unit: string; vatRate?: number | null; vatRateOnSite?: number | null; fiskalproVatRate?: number | null }
  }>

}

type ProductionPlan = {
  date: string
  orderCount: number
  stores: Array<{
    storeId: string
    storeName: string
    orderCount: number
    products: Array<{ productId: string; name: string; unit: string; quantity: number }>
    orders: Array<{
      id: string
      orderNumber: string
      status: string
      paymentStatus: string
      deliveryMethod: string
      totalCents: number
      confirmedFulfillmentAt: string
      items: Array<{ productId: string; name: string; quantity: number; unit: string }>
    }>
  }>
}



type ProductImage = {

  id: string

  productId: string

  url: string

  isMain: boolean

  isAdminMain?: boolean

  visible: boolean

  sortOrder: number

}



type Product = {

  id: string

  sku?: string | null

  plu?: string | null

  name: string

  category: string

  unit: string

  priceCents: number

  costCents: number

  active: boolean

  description?: string | null
  allergens?: string | null
  glutenFree?: boolean
  lactoseFree?: boolean
  eshopVisible?: boolean
  shopCategories?: Array<{ categoryId: string }>
  weightGrams?: number | null
  volumeMl?: number | null
  shelfLife?: string | null
  minOrderQuantity?: number
  vatRate?: number | null
  vatRateOnSite?: number | null
  fiskalproVatRate?: number | null
  b2bPricingMode?: string | null
  b2bDiscountPercent?: number | null
  b2bFixedPriceCents?: number | null
  allowPickup?: boolean
  allowLocalDelivery?: boolean
  allowShipping?: boolean
  variants?: Array<{ id: string; name: string; priceCents: number; weightGrams: number | null; volumeMl: number | null; fiskalproCode: string | null; active: boolean }>
  fiskalproArticleId?: number | string | null
  fiskalproCode?: string | null
  images?: ProductImage[]
  isBundle?: boolean
  bundleItems?: Array<{ id: string; componentProductId: string; quantity: number; component: { id: string; name: string; unit: string } }>

}



type VariantDraft = { id?: string; name: string; price: string; weight: string; volume: string; code: string; active: boolean }
type BundleItemDraft = { componentProductId: string; quantity: string }

const DELIVERY_OPTIONS = [
  { key: 'allowPickup', icon: '🏠', label: 'Osobný odber', hint: 'v predajni' },
  { key: 'allowLocalDelivery', icon: '🚗', label: 'Doručenie na adresu', hint: 'rozvoz v okrese, inak nacenenie' },
  { key: 'allowShipping', icon: '📦', label: 'Kuriér – balík', hint: 'po Slovensku (pralinky, čokolády)' },
] as const

type Store = {
  id: string
  name: string
  address: string | null
  active: boolean
  openingHours?: OpeningHours | null
  fiskalproStoreId: string | null
  fiskalproPosMappings: Array<{
    id: string
    fiskalproPosId: number
    name: string
    posNumber: number | null
  }>
}

type FiskalProShop = {
  id: number
  name: string
  city: string | null
  pointsOfSale: Array<{ id: number; name: string; posNumber: number | null }>
}

type StoreDraft = {
  name: string
  address: string
  fiskalproStoreId: string
  fiskalproPosIds: number[]
}

type StockBalance = {
  id: string
  productId: string
  storeId: string
  quantity: number | string
  updatedAt: string
  product: { id: string; name: string; unit: string }
}

type StockMovement = {
  id: string
  storeId: string
  type: string
  quantity: number | string
  note: string | null
  reference: string | null
  createdAt: string
  product: { id: string; name: string; unit: string }
  store: { id: string; name: string }
}

const STORES_PAGE_LABEL = "Prevádzky"

const STOCK_PAGE_LABEL = "Sklad (lokálny)"

const STOCK_PAGES: Record<StockView, string> = { overview: 'Stav skladu', documents: 'Skladové doklady', reports: 'Skladové zostavy' }

const SALES_PAGES: Record<SalesView, string> = { documents: 'Odberateľské doklady', partners: 'Adresár' }

const ICONS: Record<string, string> = {
  maintenance: 'M22.7 19 13.6 9.9c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z',
  salesdocs: 'M5 3h10l4 4v14H5V3zm9 1.5V8h3.5L14 4.5zM8 11h8v2H8v-2zm0 4h5v2H8v-2zm9 1 2 2-2 2v-1.5h-3v-1h3V16z',
  partners: 'M4 3h13a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4V3zm2 2v14h11V5H6zm5.5 2a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM8 16c0-1.7 1.6-3 3.5-3s3.5 1.3 3.5 3v1H8v-1zM20 6h1.5v3H20V6zm0 5h1.5v3H20v-3z',
  stockdocs: 'M7 2h10a2 2 0 0 1 2 2v17l-3-2-2 2-2-2-2 2-2-2-3 2V4a2 2 0 0 1 2-2zm1 5v2h8V7H8zm0 4v2h8v-2H8zm0 4v2h5v-2H8z',
  stockreports: 'M4 20V10h3v10H4zm6 0V4h3v16h-3zm6 0v-7h3v7h-3z',
  dashboard: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z',
  orders: 'M7 4h10l1 3h3v2h-1.2l-1.6 10.4A2 2 0 0 1 16.2 21H7.8a2 2 0 0 1-2-1.6L4.2 9H3V7h3l1-3zm-.8 5 1.6 10h8.4l1.6-10H6.2zM9 11h2v6H9v-6zm4 0h2v6h-2v-6z',
  products: 'M12 3c3 0 5 1.6 5 4.2 0 .6-.1 1.1-.3 1.6H19a2 2 0 0 1 2 2.3l-1.3 8A2 2 0 0 1 17.7 21H6.3a2 2 0 0 1-2-1.9l-1.3-8A2 2 0 0 1 5 8.8h2.3A4.4 4.4 0 0 1 7 7.2C7 4.6 9 3 12 3zm0 2c-1.8 0-3 .8-3 2.2 0 .6.2 1.1.6 1.6h4.8c.4-.5.6-1 .6-1.6C15 5.8 13.8 5 12 5z',
  categories: 'M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 3.5a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z',
  stock: 'M12 2 3 6.5v11L12 22l9-4.5v-11L12 2zm0 2.2 6.6 3.3L12 10.8 5.4 7.5 12 4.2zM5 9.1l6 3v7.4l-6-3V9.1zm8 10.4v-7.4l6-3v7.4l-6 3z',
  customers: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4.4 0-8 2.2-8 5v1h16v-1c0-2.8-3.6-5-8-5z',
  invoices: 'M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm8 1.5V8h4.5L14 3.5zM8 12h8v2H8v-2zm0 4h8v2H8v-2z',
  delivery: 'M3 5h11v3h4l3 4v5h-2a3 3 0 0 1-6 0H9a3 3 0 0 1-6 0H1V7a2 2 0 0 1 2-2zm13 5v2h3.4L18 10h-2zM6 16a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm10 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  stores: 'M4 4h16l1 5a3 3 0 0 1-2 2.8V20H5v-8.2A3 3 0 0 1 3 9l1-5zm3 9v5h4v-5H7zm7 0v5h3v-5h-3z',
  design: 'M12 3a9 9 0 0 0 0 18c1 0 1.7-.8 1.7-1.7 0-.4-.2-.8-.4-1.1-.3-.3-.4-.7-.4-1.1 0-.9.8-1.7 1.7-1.7H16a5 5 0 0 0 5-5C21 6.6 17 3 12 3zm-5.5 9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm3-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm3 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z',
  print: 'M7 3h10v4H7V3zM5 8h14a2 2 0 0 1 2 2v6h-4v4H7v-4H3v-6a2 2 0 0 1 2-2zm4 7v3h6v-3H9zm8-5a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  settings: 'M19.4 13a7.6 7.6 0 0 0 0-2l2.1-1.6-2-3.4-2.5 1a7.4 7.4 0 0 0-1.7-1L15 3.3h-4l-.4 2.7a7.4 7.4 0 0 0-1.7 1l-2.5-1-2 3.4L6.6 11a7.6 7.6 0 0 0 0 2l-2.2 1.6 2 3.4 2.5-1a7.4 7.4 0 0 0 1.7 1l.4 2.7h4l.4-2.7a7.4 7.4 0 0 0 1.7-1l2.5 1 2-3.4-2.2-1.6zM13 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z',
  pricetags: 'M12.4 2.6 20 10.2a2 2 0 0 1 0 2.8l-6.6 6.6a2 2 0 0 1-2.8 0L3 12V3h9l.4-.4zM7 8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  stats: 'M4 20h16v2H4v-2zm1-6h3v5H5v-5zm5-5h3v10h-3V9zm5-6h3v16h-3V3z',
}

const menuGroups: Array<{ label: string; items: Array<[string, string]> }> = [
  { label: 'Prevádzka', items: [['dashboard', 'Prehľad'], ['orders', 'Objednávky'], ['stats', 'Štatistiky']] },
  { label: 'Sortiment', items: [['products', 'Produkty'], ['categories', 'Kategórie'], ['pricetags', 'Cenovky']] },
  { label: 'Sklad', items: [['stock', STOCK_PAGES.overview], ['stockdocs', STOCK_PAGES.documents], ['stockreports', STOCK_PAGES.reports]] },
  { label: 'Predaj', items: [['customers', 'Zákazníci'], ['invoices', 'Faktúry'], ['salesdocs', SALES_PAGES.documents], ['partners', SALES_PAGES.partners], ['delivery', 'Cenník dopravy']] },
  { label: 'Nastavenia', items: [['design', 'Vzhľad e-shopu'], ['maintenance', MAINTENANCE_PAGE], ['print', 'Tlačové zostavy'], ['stores', 'Prevádzky']] },
]

const PAGE_INFO: Record<string, string> = {
  'Prehľad': 'Čo treba dnes pripraviť a čo si vyžaduje pozornosť.',
  'Objednávky': 'Objednávky zákazníkov, termíny výdaja a výrobný plán.',
  'Štatistiky': 'Tržby, výkon produktov a zákazníci za zvolené obdobie.',
  'Produkty': 'Sortiment e-shopu – ceny, kategórie, alergény a viditeľnosť.',
  'Kategórie': 'Kategórie a podkategórie, ktoré vidia zákazníci v e-shope.',
  'Cenovky': 'Tlač cenoviek na pult – vyberte produkty, polia, veľkosť aj formát tlače.',
  [STOCK_PAGE_LABEL]: 'Lokálne zostatky a pohyby na sklade prevádzok.',
  'Stav skladu': 'Zostatky a hodnota zásob na skladoch, minimá a skladové karty.',
  'Skladové doklady': 'Príjemky, výdajky, prevodky a inventúry – vystavenie, prevzatie, storno a tlač.',
  'Skladové zostavy': 'Obratová súpiska, stav skladu a skladová karta na tlač alebo do PDF.',
  'Zákazníci': 'Registrovaní zákazníci, firemné údaje a vernostné zľavy.',
  'Faktúry': 'Faktúry a dobropisy k platbám kartou, export pre účtovníčku.',
  'Odberateľské doklady': 'Dodacie listy, faktúry firmám (aj z viacerých dodacích listov), dobropisy a úhrady s QR platbou.',
  'Adresár': 'Firmy a odberatelia – fakturačné údaje, splatnosť a zľava.',
  [MAINTENANCE_PAGE]: 'Keď e-shop pripravujete alebo prerábate, zákazníci uvidia iba stránku „Pripravujeme“ s kontaktom a formulárom.',
  'Cenník dopravy': 'Ceny doručenia kuriérom podľa obce.',
  'Prevádzky': 'Názvy a adresy prevádzok a ich prepojenie s pokladňou FiskalPRO.',
  'Tlačové zostavy': 'Čo sa vytlačí pri položkách jednotlivých dokladov – alergény, hmotnosť, trvanlivosť, zľava a ceny.',
  'Vzhľad e-shopu': 'Farby, písmo, logo, texty a sekcie e-shopu s okamžitým náhľadom. Zákazníci zmeny uvidia až po zverejnení.',
}

function NavIcon({ name }: { name: string }) {
  return <svg className="nav-svg" viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[name] ?? ICONS.dashboard} /></svg>
}



const euro = (cents: number) =>

  (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })

const bratislavaDate = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bratislava', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date)
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day}`
}



async function adminFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers)
  const response = await fetch(input, { ...init, headers, credentials: 'include' })
  if (response.status === 401) {
    window.dispatchEvent(new Event('emili-admin-session-expired'))
  }
  return response
}

const paymentLabels: Record<string, string> = { UNPAID: 'Nezaplatené', PENDING: 'Čaká na platbu', FAILED: 'Platba zlyhala', PAID: 'Zaplatené', EXPIRED: 'Nezaplatené – zrušené' }
function paymentLabel(order: { paymentStatus?: string; paymentMethod?: string }) {
  const status = paymentLabels[order.paymentStatus ?? ''] ?? order.paymentStatus ?? '—'
  return order.paymentMethod === 'CARD_ONLINE' ? `${status} (karta online)` : order.paymentMethod === 'CASH_ON_DELIVERY' ? `${status} (dobierka)` : status
}

function App() {

  const [page, setPage] = useState('Prehľad')
  const [maintenanceTick, setMaintenanceTick] = useState(0)
  const [adminSignedIn, setAdminSignedIn] = useState(false)
  const [adminAuthChecking, setAdminAuthChecking] = useState(true)
  const [adminUsername, setAdminUsername] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [adminAuthError, setAdminAuthError] = useState('')
  const [adminAuthLoading, setAdminAuthLoading] = useState(false)

  const [orders, setOrders] = useState<Order[]>([])

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [fiskalProSendLoading, setFiskalProSendLoading] = useState(false)
  const [fiskalProSendError, setFiskalProSendError] = useState("")
  const [workflowError, setWorkflowError] = useState("")
  const [, setAlternativeAcceptanceToken] = useState("")
  const [alternativeDate, setAlternativeDate] = useState("")
  const [orderStoreFilter, setOrderStoreFilter] = useState("")
  const [orderStatusFilter, setOrderStatusFilter] = useState("")
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState("")
  const [orderDateFrom, setOrderDateFrom] = useState("")
  const [orderDateTo, setOrderDateTo] = useState("")
  const [ordersPage, setOrdersPage] = useState(1)
  const [ordersTotal, setOrdersTotal] = useState(0)
  const [ordersTab, setOrdersTab] = useState<'list' | 'plan' | 'season'>('list')
  const [newOrderOpen, setNewOrderOpen] = useState(false)
  const [newOrderProducts, setNewOrderProducts] = useState<StockProduct[] | null>(null)
  const [newOrderCategories, setNewOrderCategories] = useState<StockCategory[]>([])
  useEffect(() => { if (newOrderOpen && !newOrderProducts) void loadCatalog().then(c => { setNewOrderProducts(c.products); setNewOrderCategories(c.categories) }) }, [newOrderOpen, newOrderProducts])
  const [seasonalOnly, setSeasonalOnly] = useState(false)
  const [printSeasonalOnly, setPrintSeasonalOnly] = useState(false)
  const [previewFormat, setPreviewFormat] = useState<'a5' | 'a4'>('a5')
  const [vatBusy, setVatBusy] = useState(false)
  const [vatError, setVatError] = useState('')
  const [vatWarning, setVatWarning] = useState('')
  const [productionPlan, setProductionPlan] = useState<ProductionPlan | null>(null)
  const [productionPlanDate, setProductionPlanDate] = useState(() => bratislavaDate())
  const [productionPlanStoreId, setProductionPlanStoreId] = useState('')
  const [productionPlanLoading, setProductionPlanLoading] = useState(false)
  const [productionPlanError, setProductionPlanError] = useState('')
  const [stores, setStores] = useState<Store[]>([])
  const [storeDrafts, setStoreDrafts] = useState<Record<string, StoreDraft>>({})
  const [storeError, setStoreError] = useState("")
  const [storeLoading, setStoreLoading] = useState(false)
  const [fiskalProShops, setFiskalProShops] = useState<FiskalProShop[]>([])
  const [fiskalProShopsLoading, setFiskalProShopsLoading] = useState(false)
  const [savingStoreId, setSavingStoreId] = useState<string | null>(null)
  const [stockBalances, setStockBalances] = useState<StockBalance[]>([])
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([])
  const [selectedStockStoreId, setSelectedStockStoreId] = useState("")
  const [stockLoading, setStockLoading] = useState(false)
  const [stockError, setStockError] = useState("")
  const [stockProductSearch, setStockProductSearch] = useState("")

  const [products, setProducts] = useState<Product[]>([])

  const [productImages, setProductImages] = useState<ProductImage[]>([])

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const [imageLoading, setImageLoading] = useState(false)

  const [error, setError] = useState('')

  const [productError, setProductError] = useState('')

  const [loading, setLoading] = useState(false)

  const [search, setSearch] = useState('')


  const [showForm, setShowForm] = useState(false)
  const [copyPickMode, setCopyPickMode] = useState(false)

  const [saving, setSaving] = useState(false)

  const [editingId, setEditingId] = useState<string | null>(null)

  const [form, setForm] = useState({

    name: '', category: '', unit: 'ks', price: '', cost: '',

    sku: '', plu: '', description: '', allergens: '', glutenFree: false, lactoseFree: false, active: true, eshopVisible: true, shopCategoryIds: [] as string[], measureKind: 'none' as MeasureKind, weight: '', weightUnit: 'g' as WeightUnit, volume: '', volumeUnit: 'ml' as VolumeUnit, shelfValue: '', shelfUnit: 'DAY' as ShelfUnit, shelfNote: '', minOrder: '1', vatRate: '', vatRateOnSite: '', allowPickup: true, allowLocalDelivery: true, allowShipping: false, variants: [] as VariantDraft[], isBundle: false, bundleItems: [] as BundleItemDraft[], b2bMode: 'NONE' as B2bPricingMode, b2bDiscount: '', b2bFixed: '',

  })

  const [bundleFilter, setBundleFilter] = useState({ search: '', category: '' })

  async function signIn(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setAdminAuthLoading(true)
    setAdminAuthError('')
    try {
      const response = await fetch(`${API}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: adminUsername, password: adminPassword }),
        credentials: 'include',
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || data?.authenticated !== true) {
        throw new Error(data?.error || 'Prihlásenie sa nepodarilo.')
      }
      setAdminPassword('')
      setAdminSignedIn(true)
    } catch (error) {
      setAdminAuthError(error instanceof Error ? error.message : 'Prihlásenie sa nepodarilo.')
    } finally {
      setAdminAuthLoading(false)
    }
  }

  function signOut(message = '') {
    void fetch(`${API}/api/admin/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => undefined)
    setAdminSignedIn(false)
    setAdminAuthChecking(false)
    setAdminPassword('')
    setAdminAuthError(message)
  }

  useEffect(() => {
    let active = true
    fetch(`${API}/api/admin/auth/session`, { credentials: 'include' })
      .then((response) => {
        if (active && response.ok) setAdminSignedIn(true)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setAdminAuthChecking(false)
      })
    return () => { active = false }
  }, [])

  useEffect(() => {
    const onSessionExpired = () => signOut('Prihlásenie vypršalo. Prihlás sa znova.')
    window.addEventListener('emili-admin-session-expired', onSessionExpired)
    return () => window.removeEventListener('emili-admin-session-expired', onSessionExpired)
  }, [])

  async function loadOrders(page = ordersPage) {

    setLoading(true)

    setError('')

    setOrders([])

    try {

      const params = new URLSearchParams({ page: String(page), pageSize: '50' })
      if (search.trim()) params.set('q', search.trim())
      if (orderStoreFilter) params.set('storeId', orderStoreFilter)
      if (orderStatusFilter) params.set('status', orderStatusFilter)
      if (scheduleStatusFilter) params.set('scheduleStatus', scheduleStatusFilter)
      if (seasonalOnly) params.set('seasonal', '1')
      if (orderDateFrom) params.set('dateFrom', dayStartIso(orderDateFrom))
      if (orderDateTo) params.set('dateTo', dayEndIso(orderDateTo))
      const response = await adminFetch(`${API}/api/orders?${params.toString()}`)

      if (!response.ok) throw new Error()

      const data = await response.json()

      const loadedOrders = Array.isArray(data)
        ? data
        : Array.isArray(data?.orders) ? data.orders : []

      setOrders(loadedOrders)
      setOrdersTotal(typeof data?.total === 'number' ? data.total : loadedOrders.length)
      setOrdersPage(typeof data?.page === 'number' ? data.page : page)

    } catch {

      setOrders([])

      setError('Nepodarilo sa načítať objednávky. Skontroluj backend a databázu.')

    } finally {

      setLoading(false)

    }

  }

  async function loadProductionPlan() {
    setProductionPlanLoading(true)
    setProductionPlanError('')
    setProductionPlan(null)
    try {
      const params = new URLSearchParams({ date: productionPlanDate })
      if (productionPlanStoreId) params.set('storeId', productionPlanStoreId)
      const response = await adminFetch(`${API}/api/admin/orders/production-plan?${params.toString()}`)
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data?.error || 'Denný prehľad sa nepodarilo načítať.')
      setProductionPlan(data as ProductionPlan)
    } catch (error) {
      setProductionPlanError(error instanceof Error ? error.message : 'Denný prehľad sa nepodarilo načítať.')
      setProductionPlan(null)
    } finally {
      setProductionPlanLoading(false)
    }
  }

  async function runOrderWorkflowAction(path: string, body?: Record<string, unknown>) {
    if (!selectedOrder) return
    setWorkflowError('')
    try {
      const response = await adminFetch(`${API}/api/admin/orders/${encodeURIComponent(selectedOrder.id)}/${path}`, {
        method: 'POST',
        ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) throw new Error(data?.error || 'Zmenu objednávky sa nepodarilo uložiť.')
      if (typeof data?.acceptanceToken === 'string') setAlternativeAcceptanceToken(data.acceptanceToken)
      if (data?.order) setSelectedOrder(data.order)
      await loadOrders()
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Zmenu objednávky sa nepodarilo uložiť.')
    }
  }

  const [paymentBusy, setPaymentBusy] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [refundAmount, setRefundAmount] = useState('')
  async function reloadSelectedOrder(id: string) {
    await loadOrders(ordersPage)
    const response = await adminFetch(`${API}/api/orders?q=${encodeURIComponent(selectedOrder?.orderNumber ?? '')}`)
    const data = await response.json().catch(() => null)
    const fresh = (data?.orders ?? []).find((order: Order) => order.id === id)
    if (fresh) setSelectedOrder(fresh)
  }
  async function syncPayment() {
    if (!selectedOrder) return
    setPaymentBusy(true); setPaymentError('')
    try {
      await adminRequest(`/api/admin/orders/${encodeURIComponent(selectedOrder.id)}/payment-sync`, { method: 'POST' })
      await reloadSelectedOrder(selectedOrder.id)
    } catch (e) { setPaymentError(e instanceof Error ? e.message : 'Overenie platby zlyhalo.') } finally { setPaymentBusy(false) }
  }
  async function refundPayment() {
    if (!selectedOrder) return
    const remaining = selectedOrder.totalCents - (selectedOrder.refundedCents ?? 0)
    const amountCents = refundAmount.trim() ? Math.round(Number(refundAmount.replace(',', '.')) * 100) : remaining
    if (!Number.isFinite(amountCents) || amountCents <= 0) { setPaymentError('Zadaj platnú sumu.'); return }
    if (!window.confirm(`Naozaj vrátiť zákazníkovi ${euro(amountCents)} na kartu? Vystaví sa dobropis.`)) return
    setPaymentBusy(true); setPaymentError('')
    try {
      await adminRequest(`/api/admin/orders/${encodeURIComponent(selectedOrder.id)}/refund`, { method: 'POST', body: JSON.stringify({ amountCents }) })
      setRefundAmount('')
      await reloadSelectedOrder(selectedOrder.id)
    } catch (e) { setPaymentError(e instanceof Error ? e.message : 'Vrátenie platby zlyhalo.') } finally { setPaymentBusy(false) }
  }

  // DPH položiek: so sebou / na mieste (iba administrácia; po vystavení faktúry sa nemení)
  const itemVatEditable = Boolean(selectedOrder && !['CANCELLED', 'REJECTED'].includes(selectedOrder.status) && !selectedOrder.invoices?.some(i => i.kind === 'INVOICE'))
  async function setItemsVat(items: Array<{ id: string; onSite: boolean }>) {
    if (!selectedOrder) return
    setVatBusy(true); setVatError(''); setVatWarning('')
    try {
      const data = await adminRequest(`/api/admin/orders/${encodeURIComponent(selectedOrder.id)}/items-vat`, { method: 'POST', body: JSON.stringify({ items }) })
      const patch = { items: data.items }
      setSelectedOrder(current => current && current.id === selectedOrder.id ? { ...current, ...patch } : current)
      setOrders(current => current.map(o => o.id === selectedOrder.id ? { ...o, ...patch } : o))
      if (data.warning) setVatWarning(data.warning)
    } catch (e) { setVatError(e instanceof Error ? e.message : 'Zmena DPH sa nepodarila.') } finally { setVatBusy(false) }
  }

  async function changeOrderStatus(status: string) {
    if (!selectedOrder) return
    setWorkflowError('')
    try {
      const response = await adminFetch(`${API}/api/orders/${encodeURIComponent(selectedOrder.id)}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) throw new Error(data?.error || 'Stav objednávky sa nepodarilo zmeniť.')
      setSelectedOrder(data)
      await loadOrders()
    } catch (error) {
      setWorkflowError(error instanceof Error ? error.message : 'Stav objednávky sa nepodarilo zmeniť.')
    }
  }

  function openOrder(order: Order) {
    setWorkflowError('')
    setAlternativeAcceptanceToken('')
    setAlternativeDate(order.proposedFulfillmentAt ? (() => { const d = new Date(order.proposedFulfillmentAt!); return `${toIsoDay(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` })() : '')
    setSelectedOrder(order)
  }

  async function sendOrderToFiskalPro(order: Order) {
    if (order.fiskalproOrderId || order.fiskalproStatus || fiskalProSendLoading) return
    const confirmed = window.confirm(
      "Odoslať jednu objednávku " + order.orderNumber + " (" + euro(order.totalCents) + ") do Kanianky (shop 73633), na C9 (POS 113148) ako odložený doklad? Táto akcia nevystaví ani nedokončí predaj. Ak výsledok nebude jednoznačný, ďalšie odoslanie sa zablokuje.",
    )
    if (!confirmed) return

    setFiskalProSendLoading(true)
    setFiskalProSendError("")
    try {
      const response = await adminFetch(
        `${API}/api/admin/orders/` + encodeURIComponent(order.id) + "/fiskalpro-send",
        { method: "POST" },
      )
      const result = await response.json().catch(() => null)
      const status = typeof result?.status === "string" ? result.status : "UNKNOWN"
      setSelectedOrder((current) => current?.id === order.id
        ? { ...current, fiskalproStatus: status, fiskalproOrderId: result?.documentId ?? null }
        : current)
      if (!response.ok) {
        throw new Error(result?.message || result?.error || "Výsledok odoslania nie je potvrdený. Objednávku neposielaj znova.")
      }
      await loadOrders()
    } catch (sendError) {
      setSelectedOrder((current) => current?.id === order.id
        ? { ...current, fiskalproStatus: current.fiskalproStatus || "UNKNOWN" }
        : current)
      setFiskalProSendError(sendError instanceof Error
        ? sendError.message
        : "Výsledok odoslania nie je potvrdený. Objednávku neposielaj znova.")
      void loadOrders()
    } finally {
      setFiskalProSendLoading(false)
    }
  }



  async function loadStores() {
    setStoreLoading(true)
    setStoreError("")
    try {
      const response = await adminFetch(`${API}/api/admin/stores`)
      const data = await response.json().catch(() => null)
      if (!response.ok || !Array.isArray(data)) {
        throw new Error(data?.error || "Predajne sa nepodarilo načítať.")
      }
      setStores(data)
      setStoreDrafts(Object.fromEntries(data.map((store: Store) => [store.id, {
        name: store.name,
        address: store.address ?? "",
        fiskalproStoreId: store.fiskalproStoreId ?? "",
        fiskalproPosIds: (store.fiskalproPosMappings ?? []).map((mapping) => mapping.fiskalproPosId),
      }])))
    } catch (error) {
      setStoreError(error instanceof Error ? error.message : "Predajne sa nepodarilo načítať.")
    } finally {
      setStoreLoading(false)
    }
  }

  async function loadFiskalProShops() {
    setFiskalProShopsLoading(true)
    setStoreError("")
    try {
      const response = await adminFetch(`${API}/api/integrations/fiskalpro/shops`)
      const data = await response.json().catch(() => null)
      if (!response.ok || !Array.isArray(data?.shops)) {
        throw new Error(data?.error || "Overený zoznam FiskalPRO sa nepodarilo načítať.")
      }
      const shops = data.shops.map((shop: any) => {
        if (!Number.isSafeInteger(shop?.id) || typeof shop?.name !== "string" || !Array.isArray(shop?.pointsOfSale)) {
          throw new Error("Zoznam FiskalPRO obsahuje neplatné údaje.")
        }
        const pointsOfSale = shop.pointsOfSale.map((pos: any) => {
          if (!Number.isSafeInteger(pos?.id) || typeof pos?.name !== "string") {
            throw new Error("Zoznam FiskalPRO obsahuje neplatné údaje pokladnice.")
          }
          return { id: pos.id, name: pos.name, posNumber: Number.isSafeInteger(pos.posNumber) ? pos.posNumber : null }
        })
        return { id: shop.id, name: shop.name, city: typeof shop.city === "string" ? shop.city : null, pointsOfSale }
      }) as FiskalProShop[]
      setFiskalProShops(shops)
    } catch (error) {
      setFiskalProShops([])
      setStoreError(error instanceof Error ? error.message : "Overený zoznam FiskalPRO sa nepodarilo načítať.")
    } finally {
      setFiskalProShopsLoading(false)
    }
  }

  async function saveStore(storeId: string) {
    const draft = storeDrafts[storeId]
    const existingStore = stores.find((store) => store.id === storeId)
    if (!draft || !draft.name.trim()) {
      setStoreError("Názov predajne nesmie byť prázdny.")
      return
    }
    if (!existingStore) return

    const oldPosIds = (existingStore.fiskalproPosMappings ?? []).map((mapping) => mapping.fiskalproPosId).sort((a, b) => a - b)
    const newPosIds = [...draft.fiskalproPosIds].sort((a, b) => a - b)
    const mappingChanged = draft.fiskalproStoreId !== (existingStore.fiskalproStoreId ?? "") ||
      JSON.stringify(oldPosIds) !== JSON.stringify(newPosIds)
    const selectedShop = draft.fiskalproStoreId
      ? fiskalProShops.find((shop) => String(shop.id) === draft.fiskalproStoreId)
      : undefined
    if (mappingChanged && draft.fiskalproStoreId && !selectedShop) {
      setStoreError("Najprv načítaj aktuálny zoznam FiskalPRO a vyber prevádzku zo zoznamu.")
      return
    }
    if (mappingChanged && selectedShop) {
      const registeredPosIds = new Set(selectedShop.pointsOfSale.map((pos) => pos.id))
      if (!draft.fiskalproPosIds.length || draft.fiskalproPosIds.some((posId) => !registeredPosIds.has(posId))) {
        setStoreError("Vyber aspoň jednu pokladnicu patriacu k zvolenej FiskalPRO prevádzke.")
        return
      }
    }
    if (mappingChanged) {
      const selectedPosNames = selectedShop?.pointsOfSale
        .filter((pos) => draft.fiskalproPosIds.includes(pos.id))
        .map((pos) => `${pos.name} (POS ${pos.id})`).join(", ") || "bez priradenej pokladnice"
      const target = selectedShop
        ? `${selectedShop.name} (shop ${selectedShop.id}); pokladnice: ${selectedPosNames}`
        : "zrušiť priradenie FiskalPRO"
      if (!window.confirm(`Potvrď priradenie lokálnej predajne „${existingStore.name}“ (interné ID ${existingStore.id}) na ${target}.`)) return
    }

    setSavingStoreId(storeId)
    setStoreError("")
    try {
      const response = await adminFetch(`${API}/api/admin/stores/${encodeURIComponent(storeId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: draft.name,
          address: draft.address.trim() || null,
          ...(mappingChanged ? {
            fiskalproStoreId: draft.fiskalproStoreId || null,
            fiskalproPosIds: draft.fiskalproPosIds,
          } : {}),
        }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) throw new Error(data?.error || "Uloženie predajne sa nepodarilo.")
      setStores((current) => current.map((store) => store.id === storeId ? data : store))
      setStoreDrafts((current) => ({ ...current, [storeId]: {
        name: data.name,
        address: data.address ?? "",
        fiskalproStoreId: data.fiskalproStoreId ?? "",
        fiskalproPosIds: (data.fiskalproPosMappings ?? []).map((mapping: Store["fiskalproPosMappings"][number]) => mapping.fiskalproPosId),
      } }))
    } catch (error) {
      setStoreError(error instanceof Error ? error.message : "Uloženie predajne sa nepodarilo.")
    } finally {
      setSavingStoreId(null)
    }
  }

  async function loadLocalStock(storeId: string) {
    if (!storeId) return
    setSelectedStockStoreId(storeId)
    setStockLoading(true)
    setStockError("")
    try {
      const [balancesResponse, movementsResponse] = await Promise.all([
        adminFetch(`${API}/api/stock`),
        adminFetch(`${API}/api/stock/movements?storeId=${encodeURIComponent(storeId)}`),
      ])
      const balances = await balancesResponse.json().catch(() => null)
      const movements = await movementsResponse.json().catch(() => null)
      if (!balancesResponse.ok || !Array.isArray(balances)) {
        throw new Error(balances?.error || "Lokálne zostatky sa nepodarilo načítať.")
      }
      if (!movementsResponse.ok || !Array.isArray(movements)) {
        throw new Error(movements?.error || "Skladové pohyby sa nepodarilo načítať.")
      }
      setStockBalances(balances.filter((balance: StockBalance) => balance.storeId === storeId))
      setStockMovements(movements)
    } catch (error) {
      setStockError(error instanceof Error ? error.message : "Lokálny sklad sa nepodarilo načítať.")
      setStockBalances([])
      setStockMovements([])
    } finally {
      setStockLoading(false)
    }
  }

  async function loadProducts() {

    setProductError('')

    try {

      const response = await adminFetch(`${API}/api/products`)

      if (!response.ok) throw new Error()

      const data = await response.json()

      setProducts(Array.isArray(data) ? data : data.products ?? [])

    } catch {

      setProductError('Produkty sa nepodarilo načítať. Skontroluj backend.')

    }

  }



async function loadProductImages(productId: string) {

  try {

    const response = await adminFetch(

      `${API}/api/products/${productId}/images`

    )

    if (!response.ok) throw new Error()

    setProductImages(await response.json())

  } catch {

    setProductError('Fotografie sa nepodarilo načítať.')

  }

}



async function uploadProductImages() {

  if (!editingId || selectedFiles.length === 0) return



  setImageLoading(true)

  setProductError('')



  try {

    const data = new FormData()

    selectedFiles.forEach(file => data.append('images', file))



    const response = await adminFetch(

      `${API}/api/products/${editingId}/images`,

      { method: 'POST', body: data }

    )



    const result = await response.json().catch(() => null)

    if (!response.ok) {

      throw new Error(result?.error || 'Fotografie sa nepodarilo nahrať.')

    }



    setSelectedFiles([])

    await loadProductImages(editingId)

  } catch (err) {

    setProductError(

      err instanceof Error ? err.message : 'Nahrávanie fotografií zlyhalo.'

    )

  } finally {

    setImageLoading(false)

  }

}



async function updateProductImage(

  image: ProductImage,

  changes: { isMain?: boolean; isAdminMain?: boolean; visible?: boolean }

) {

  if (!editingId) return



  try {

    const response = await adminFetch(

      `${API}/api/products/${editingId}/images/${image.id}`,

      {

        method: 'PATCH',

        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(changes),

      }

    )



    const result = await response.json().catch(() => null)

    if (!response.ok) {

      throw new Error(result?.error || 'Zmenu fotografie sa nepodarilo uložiť.')

    }



    await loadProductImages(editingId)

  } catch (err) {

    setProductError(

      err instanceof Error ? err.message : 'Zmena fotografie zlyhala.'

    )

  }

}



async function deleteProduct() {
  if (!editingId) return
  if (!window.confirm(`Naozaj chceš odstrániť produkt „${form.name}“? Túto akciu nemožno vrátiť späť.`)) return

  setProductError('')
  try {
    const response = await adminFetch(`${API}/api/products/${editingId}`, { method: 'DELETE' })
    const result = await response.json().catch(() => null)
    if (!response.ok) throw new Error(result?.error || 'Produkt sa nepodarilo odstrániť.')
    resetForm()
    await loadProducts()
  } catch (err) {
    setProductError(err instanceof Error ? err.message : 'Odstránenie produktu zlyhalo.')
  }
}


async function deleteProductImage(image: ProductImage) {

  if (!editingId) return

  if (!window.confirm('Naozaj chceš odstrániť túto fotografiu?')) return



  try {

    const response = await adminFetch(

      `${API}/api/products/${editingId}/images/${image.id}`,

      { method: 'DELETE' }

    )



    const result = await response.json().catch(() => null)

    if (!response.ok) {

      throw new Error(result?.error || 'Fotografiu sa nepodarilo odstrániť.')

    }



    await loadProductImages(editingId)

  } catch (err) {

    setProductError(

      err instanceof Error ? err.message : 'Odstránenie fotografie zlyhalo.'

    )

  }

}



  useEffect(() => {
    if (!adminSignedIn) return
    void loadOrders()
    void loadStores()
    void loadProducts()
  }, [adminSignedIn])



  const [shopCategoryOptions, setShopCategoryOptions] = useState<ShopCategory[]>([])
  function loadShopCategoryOptions() {
    adminRequest('/api/admin/shop-categories')
      .then(data => setShopCategoryOptions(Array.isArray(data?.categories) ? data.categories : []))
      .catch(() => setShopCategoryOptions([]))
  }
  useEffect(() => { if (showForm || page === 'Produkty' || page === 'Cenovky') loadShopCategoryOptions() }, [showForm, page])
  useEffect(() => { if (page !== 'Produkty') setCopyPickMode(false) }, [page])

  const [fiskalGroupList, setFiskalGroupList] = useState<FiskalGroup[]>([])
  const [fiskalGroupsOpen, setFiskalGroupsOpen] = useState(false)
  const [fiskalGroupsInfo, setFiskalGroupsInfo] = useState<FiskalGroupsInfo>({ namesLoadedAt: null, canLoadNames: false })
  function loadFiskalGroups() {
    adminRequest('/api/admin/fiskal-groups').then(data => {
      setFiskalGroupList(Array.isArray(data?.groups) ? data.groups : [])
      setFiskalGroupsInfo({ namesLoadedAt: data?.namesLoadedAt ?? null, canLoadNames: Boolean(data?.canLoadNames), loadError: data?.loadError })
    }).catch(() => setFiskalGroupList([]))
  }
  useEffect(() => { if (showForm || page === 'Produkty' || page === 'Kategórie') loadFiskalGroups() }, [showForm, page, products.length])
  const groupLabel = (key: string) => fiskalGroupLabel(fiskalGroupList, key)
  // Pri ručne pridanom produkte sa vyberá iba zo skupín, ktoré naozaj existujú v pokladni.
  const fiskalGroups = useMemo(() => fiskalGroupList.filter(g => g.fromFiskal).map(g => g.key), [fiskalGroupList])
  const allergenState = parseAllergens(form.allergens)
  const editingFromFiskal = editingId ? Boolean(products.find(p => p.id === editingId)?.fiskalproArticleId) : false
  const editingProductVat = editingId ? (products.find(p => p.id === editingId)?.fiskalproVatRate ?? null) : null
  const b2bPreviewVat = form.vatRate !== '' ? Number(form.vatRate) : editingProductVat
  const b2bPreview = form.b2bMode === 'NONE' ? null : computeB2bPriceCents({
    priceCents: Math.round((Number(form.price.replace(',', '.')) || 0) * 100),
    b2bPricingMode: form.b2bMode,
    b2bDiscountPercent: form.b2bMode === 'DISCOUNT' ? (Number(form.b2bDiscount) || 0) : null,
    b2bFixedPriceCents: form.b2bMode === 'FIXED' ? Math.round((Number(form.b2bFixed.replace(',', '.')) || 0) * 100) : null,
    vatRate: b2bPreviewVat,
  })
  function updateVariant(index: number, patch: Partial<VariantDraft>) {
    setForm(current => ({ ...current, variants: current.variants.map((v, i) => i === index ? { ...v, ...patch } : v) }))
  }
  function updateBundleItem(index: number, patch: Partial<BundleItemDraft>) {
    setForm(current => ({ ...current, bundleItems: current.bundleItems.map((b, i) => i === index ? { ...b, ...patch } : b) }))
  }
  // Komplet nemôže obsahovať sám seba ani iný komplet (bez vnorených komplet-ov).
  const bundleAllOptions = useMemo(() => products.filter(p => p.id !== editingId && !p.isBundle), [products, editingId])
  const normBundle = (value: string) => value.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')
  const bundleSearchTerm = normBundle(bundleFilter.search.trim())
  const bundleComponentOptions = bundleAllOptions.filter(p =>
    (!bundleFilter.category || (p.shopCategories ?? []).some(link => link.categoryId === bundleFilter.category))
    && (!bundleSearchTerm || normBundle(p.name).includes(bundleSearchTerm)))
  const variantDrag = useDragSort('variants', form.variants.map((_, index) => String(index)),
    ids => setForm(current => ({ ...current, variants: ids.map(i => current.variants[Number(i)]).filter(Boolean) })), { handle: true })
  const imageDrag = useDragSort('images', productImages.map(image => image.id), ids => void reorderProductImages(ids), { disabled: imageLoading })
  async function reorderProductImages(imageIds: string[]) {
    if (!editingId) return
    const previous = productImages
    setProductImages(imageIds.map(id => previous.find(image => image.id === id)!).filter(Boolean).map((image, index) => ({ ...image, isMain: index === 0 })))
    try {
      const response = await adminFetch(`${API}/api/products/${editingId}/images/order`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ imageIds }) })
      const result = await response.json().catch(() => null)
      if (!response.ok) throw new Error(result?.error || 'Poradie fotografií sa nepodarilo uložiť.')
      setProductImages(result)
    } catch (err) {
      setProductImages(previous)
      setProductError(err instanceof Error ? err.message : 'Poradie fotografií sa nepodarilo uložiť.')
    }
  }

  function resetForm() {

    setForm({

      name: '', category: '', unit: 'ks', price: '', cost: '',

      sku: '', plu: '', description: '', allergens: '', glutenFree: false, lactoseFree: false, active: true, eshopVisible: true, shopCategoryIds: [] as string[], measureKind: 'none' as MeasureKind, weight: '', weightUnit: 'g' as WeightUnit, volume: '', volumeUnit: 'ml' as VolumeUnit, shelfValue: '', shelfUnit: 'DAY' as ShelfUnit, shelfNote: '', minOrder: '1', vatRate: '', vatRateOnSite: '', allowPickup: true, allowLocalDelivery: true, allowShipping: false, variants: [] as VariantDraft[], isBundle: false, bundleItems: [] as BundleItemDraft[], b2bMode: 'NONE' as B2bPricingMode, b2bDiscount: '', b2bFixed: '',

    })

    setBundleFilter({ search: '', category: '' })

    setEditingId(null)

    setShowForm(false)

  }



  function editProduct(product: Product) {
    const editWeightUnit = preferredWeightUnit([product.weightGrams, ...(product.variants ?? []).map(v => v.weightGrams)])
    const editVolumeUnit = preferredVolumeUnit([product.volumeMl, ...(product.variants ?? []).map(v => v.volumeMl)])
    const editShelf = parseShelfLife(product.shelfLife)

    setForm({

      name: product.name,

      category: product.category,

      unit: normalizeUnit(product.unit),

      price: (product.priceCents / 100).toFixed(2),

      cost: (product.costCents / 100).toFixed(2),

      sku: product.sku ?? '',

      plu: product.plu ?? '',

      description: product.description ?? '',
      allergens: product.allergens ?? '', glutenFree: product.glutenFree ?? false, lactoseFree: product.lactoseFree ?? false,
      active: product.active,
      eshopVisible: product.eshopVisible !== false,
      shopCategoryIds: (product.shopCategories ?? []).map(link => link.categoryId),
      measureKind: measureKindOf(product.weightGrams, product.volumeMl),
      weight: weightToInput(product.weightGrams, editWeightUnit),
      weightUnit: editWeightUnit,
      volume: volumeToInput(product.volumeMl, editVolumeUnit),
      volumeUnit: editVolumeUnit,
      shelfValue: editShelf.value,
      shelfUnit: editShelf.unit,
      shelfNote: editShelf.note,
      minOrder: String(product.minOrderQuantity ?? 1),
      allowPickup: product.allowPickup !== false,
      allowLocalDelivery: product.allowLocalDelivery !== false,
      allowShipping: product.allowShipping === true,
      vatRate: product.vatRate === null || product.vatRate === undefined ? '' : String(product.vatRate),
      vatRateOnSite: product.vatRateOnSite === null || product.vatRateOnSite === undefined ? '' : String(product.vatRateOnSite),
      variants: (product.variants ?? []).map(v => ({ id: v.id, name: v.name, price: (v.priceCents / 100).toFixed(2), weight: weightToInput(v.weightGrams, editWeightUnit), volume: volumeToInput(v.volumeMl, editVolumeUnit), code: v.fiskalproCode ?? '', active: v.active })),
      b2bMode: (product.b2bPricingMode as B2bPricingMode) ?? 'NONE',
      b2bDiscount: product.b2bDiscountPercent != null ? String(product.b2bDiscountPercent) : '',
      b2bFixed: product.b2bFixedPriceCents != null ? (product.b2bFixedPriceCents / 100).toFixed(2) : '',
      isBundle: product.isBundle === true,
      bundleItems: (product.bundleItems ?? []).map(b => ({ componentProductId: b.componentProductId, quantity: String(b.quantity) })),

    })

    setEditingId(product.id)

setBundleFilter({ search: '', category: '' })

setProductImages([])

setSelectedFiles([])

setShowForm(true)

void loadProductImages(product.id)

  }

  /** Predvyplní formulár „Pridať produkt“ podľa existujúceho produktu (nový produkt, bez SKU/PLU a bez fotiek originálu). */
  function copyProduct(product: Product) {
    const editWeightUnit = preferredWeightUnit([product.weightGrams, ...(product.variants ?? []).map(v => v.weightGrams)])
    const editVolumeUnit = preferredVolumeUnit([product.volumeMl, ...(product.variants ?? []).map(v => v.volumeMl)])
    const editShelf = parseShelfLife(product.shelfLife)

    setForm({
      name: `${product.name} (kópia)`,
      category: product.category,
      unit: normalizeUnit(product.unit),
      price: (product.priceCents / 100).toFixed(2),
      cost: (product.costCents / 100).toFixed(2),
      sku: '',
      plu: '',
      description: product.description ?? '',
      allergens: product.allergens ?? '', glutenFree: product.glutenFree ?? false, lactoseFree: product.lactoseFree ?? false,
      active: product.active,
      eshopVisible: product.eshopVisible !== false,
      shopCategoryIds: (product.shopCategories ?? []).map(link => link.categoryId),
      measureKind: measureKindOf(product.weightGrams, product.volumeMl),
      weight: weightToInput(product.weightGrams, editWeightUnit),
      weightUnit: editWeightUnit,
      volume: volumeToInput(product.volumeMl, editVolumeUnit),
      volumeUnit: editVolumeUnit,
      shelfValue: editShelf.value,
      shelfUnit: editShelf.unit,
      shelfNote: editShelf.note,
      minOrder: String(product.minOrderQuantity ?? 1),
      allowPickup: product.allowPickup !== false,
      allowLocalDelivery: product.allowLocalDelivery !== false,
      allowShipping: product.allowShipping === true,
      vatRate: product.vatRate === null || product.vatRate === undefined ? '' : String(product.vatRate),
      vatRateOnSite: product.vatRateOnSite === null || product.vatRateOnSite === undefined ? '' : String(product.vatRateOnSite),
      variants: (product.variants ?? []).map(v => ({ name: v.name, price: (v.priceCents / 100).toFixed(2), weight: weightToInput(v.weightGrams, editWeightUnit), volume: volumeToInput(v.volumeMl, editVolumeUnit), code: '', active: v.active })),
      b2bMode: (product.b2bPricingMode as B2bPricingMode) ?? 'NONE',
      b2bDiscount: product.b2bDiscountPercent != null ? String(product.b2bDiscountPercent) : '',
      b2bFixed: product.b2bFixedPriceCents != null ? (product.b2bFixedPriceCents / 100).toFixed(2) : '',
      isBundle: product.isBundle === true,
      bundleItems: (product.bundleItems ?? []).map(b => ({ componentProductId: b.componentProductId, quantity: String(b.quantity) })),
    })

    setEditingId(null)
    setBundleFilter({ search: '', category: '' })
    setProductImages([])
    setSelectedFiles([])
    setShowForm(true)
  }

  async function saveProduct(event: React.FormEvent<HTMLFormElement>) {

    event.preventDefault()

    setSaving(true)

    setProductError('')

    try {
      const productGrams = form.measureKind === 'weight' ? inputToGrams(form.weight, form.weightUnit) : null
      const variantGrams = form.variants.map(v => form.measureKind === 'weight' ? inputToGrams(v.weight, form.weightUnit) : null)
      if (Number.isNaN(productGrams) || variantGrams.some(g => Number.isNaN(g))) throw new Error('Hmotnosť musí byť kladné číslo.')
      const productMl = form.measureKind === 'volume' ? inputToMl(form.volume, form.volumeUnit) : null
      const variantMl = form.variants.map(v => form.measureKind === 'volume' ? inputToMl(v.volume, form.volumeUnit) : null)
      if (Number.isNaN(productMl) || variantMl.some(v => Number.isNaN(v))) throw new Error('Objem musí byť kladné číslo.')

      const payload = {

        name: form.name.trim(),

        category: form.category.trim(),

        unit: form.unit.trim() || 'ks',

        priceCents: Math.round(Number(form.price.replace(',', '.')) * 100),

        costCents: Math.round(Number(form.cost.replace(',', '.') || '0') * 100),

        sku: form.sku.trim() || undefined,

        plu: form.plu.trim() || undefined,

        description: form.description.trim() || undefined,
        allergens: form.allergens.trim() || null,
        glutenFree: form.glutenFree,
        lactoseFree: form.lactoseFree,
        active: form.active,
        eshopVisible: form.eshopVisible,
        shopCategoryIds: form.shopCategoryIds,
        weightGrams: productGrams,
        volumeMl: productMl,
        shelfLife: formatShelfLife({ value: form.shelfValue, unit: form.shelfUnit, note: form.shelfNote }) || null,
        minOrderQuantity: Math.max(1, Math.round(Number(form.minOrder) || 1)),
        vatRate: form.vatRate === '' ? null : Number(form.vatRate),
        vatRateOnSite: form.vatRateOnSite === '' ? null : Number(form.vatRateOnSite),
        allowPickup: form.allowPickup,
        allowLocalDelivery: form.allowLocalDelivery,
        allowShipping: form.allowShipping,
        variants: form.variants.map((v, index) => ({
          ...(v.id ? { id: v.id } : {}),
          name: v.name.trim(),
          priceCents: Math.round(Number(v.price.replace(',', '.')) * 100),
          weightGrams: variantGrams[index],
          volumeMl: variantMl[index],
          fiskalproCode: v.code.trim() || null,
          active: v.active,
        })),
        b2bPricingMode: form.b2bMode,
        b2bDiscountPercent: form.b2bMode === 'DISCOUNT' ? Math.max(0, Math.min(100, Math.round(Number(form.b2bDiscount) || 0))) : null,
        b2bFixedPriceCents: form.b2bMode === 'FIXED' ? Math.round((Number(form.b2bFixed.replace(',', '.')) || 0) * 100) : null,
        isBundle: form.isBundle,
        bundleItems: form.isBundle ? form.bundleItems.map(b => {
          const componentUnit = products.find(p => p.id === b.componentProductId)?.unit
          const wholeOnly = !componentUnit || normalizeUnit(componentUnit) === 'ks'
          const raw = Number(b.quantity.replace(',', '.')) || 1
          return { componentProductId: b.componentProductId, quantity: wholeOnly ? Math.max(1, Math.round(raw)) : Math.max(0.01, raw) }
        }) : [],

      }



      const response = await adminFetch(

        editingId

          ? `${API}/api/products/${editingId}`

          : `${API}/api/products`,

        {

          method: editingId ? 'PATCH' : 'POST',

          headers: { 'Content-Type': 'application/json' },

          body: JSON.stringify(payload),

        },

      )



      if (!response.ok) {

        const result = await response.json().catch(() => null)

        throw new Error(result?.error || 'Uloženie produktu sa nepodarilo.')

      }

      setProductImages([])

      setSelectedFiles([])

      resetForm()

      await loadProducts()

    } catch (err) {

      setProductError(err instanceof Error ? err.message : 'Nastala chyba pri ukladaní.')

    } finally {

      setSaving(false)

    }

  }



  // Sezónne objednávky sa presunuli z Nastavení do Objednávok (záložka Vianoce).
  useEffect(() => { if (page === 'Nastavenia') { setPage('Objednávky'); setOrdersTab('season') } }, [page])
  // Filtre objednávok sa použijú hneď (hľadanie s krátkym oneskorením počas písania).
  useEffect(() => {
    if (!adminSignedIn || page !== 'Objednávky') return
    const timer = window.setTimeout(() => void loadOrders(1), search.trim() ? 350 : 0)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, orderStoreFilter, orderStatusFilter, scheduleStatusFilter, orderDateFrom, orderDateTo, seasonalOnly])
  useEffect(() => {
    if (page === 'Objednávky' && ordersTab === 'plan' && !productionPlan && !productionPlanLoading) void loadProductionPlan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, ordersTab])
  // Prechod z Prehľadu na stránku s pripraveným filtrom (prípadne rovno otvorí objednávku).
  const [pendingOpenOrder, setPendingOpenOrder] = useState('')
  function navigateTo(target: DashboardTarget) {
    if (target.page === 'Objednávky') {
      const today = bratislavaDate()
      const tomorrow = bratislavaDate(new Date(Date.now() + 86400000))
      setOrdersTab('list')
      setOrderStoreFilter('')
      setOrderStatusFilter(target.status ?? '')
      setScheduleStatusFilter(target.scheduleStatus ?? '')
      setOrderDateFrom(target.preset === 'today' ? today : target.preset === 'tomorrow' ? tomorrow : '')
      setOrderDateTo(target.preset === 'today' ? today : target.preset === 'tomorrow' ? tomorrow : '')
      setSearch(target.orderNumber ?? '')
      setPendingOpenOrder(target.orderNumber ?? '')
      setPage('Objednávky')
      return
    }
    if (target.page === 'Produkty') {
      presetProductFilters({ issues: target.issue ? [target.issue] : [] })
      setPage('Produkty'); setSearch(''); void loadProducts(); return
    }
    if (target.page === STORES_PAGE_LABEL) { void loadStores(); void loadFiskalProShops() }
    setPage(target.page); setSearch('')
  }
  useEffect(() => {
    if (!pendingOpenOrder) return
    const match = orders.find(order => order.orderNumber === pendingOpenOrder)
    if (match) { openOrder(match); setPendingOpenOrder('') }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, pendingOpenOrder])
  const orderFiltersActive = Boolean(search.trim() || orderStoreFilter || orderStatusFilter || scheduleStatusFilter || orderDateFrom || orderDateTo || seasonalOnly)
  const clearOrderFilters = () => { setSearch(''); setOrderStoreFilter(''); setOrderStatusFilter(''); setScheduleStatusFilter(''); setOrderDateFrom(''); setOrderDateTo(''); setSeasonalOnly(false) }
  const orderDatePresets = (() => {
    const today = bratislavaDate()
    const shift = (days: number) => bratislavaDate(new Date(Date.now() + days * 86400000))
    const weekday = (new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7
    return [
      { label: 'Dnes', from: today, to: today },
      { label: 'Zajtra', from: shift(1), to: shift(1) },
      { label: 'Tento týždeň', from: shift(-weekday), to: shift(6 - weekday) },
      { label: 'Ďalších 7 dní', from: today, to: shift(7) },
    ]
  })()
  const filteredOrders = orders.filter(order =>

    `${order.orderNumber} ${order.customerName}`.toLowerCase().includes(search.toLowerCase()),

  )




  const filteredStockProducts = filterActiveStockProducts(products, stockProductSearch)



  if (adminAuthChecking) {
    return <main className="admin-login-shell"><p>Overujem administrátorskú reláciu…</p></main>
  }

  if (!adminSignedIn) {
    return (
      <main className="admin-login-shell">
        <form className="admin-login-card" onSubmit={signIn}>
          <div className="brand">Emili<span>♡</span><small>CUKRÁREŇ</small></div>
          <h1>Prihlásenie administrátora</h1>
          <p>Na správu objednávok a nastavení sa prihlás oprávneným účtom.</p>
          <label>Prihlasovacie meno
            <input autoComplete="username" value={adminUsername} onChange={(event) => setAdminUsername(event.target.value)} required />
          </label>
          <label>Heslo
            <input type="password" autoComplete="current-password" value={adminPassword} onChange={(event) => setAdminPassword(event.target.value)} required />
          </label>
          {adminAuthError && <p className="admin-login-error" role="alert">{adminAuthError}</p>}
          <button className="primary-btn" type="submit" disabled={adminAuthLoading}>
            {adminAuthLoading ? 'Prihlasujem…' : 'Prihlásiť sa'}
          </button>
        </form>
      </main>
    )
  }

  return (

    <div className="admin-shell">
      <PrintHost />

      <aside className="sidebar">

        <div className="brand">Emili<span>♡</span><small>CUKRÁREŇ</small></div>

        <nav>
          {menuGroups.map(group => <div className="nav-group" key={group.label}>
            <div className="side-label">{group.label}</div>
            {group.items.map(([icon, label]) => (
              <button key={label} className={`nav-item ${page === label ? 'active' : ''}`} aria-current={page === label ? 'page' : undefined}
                onClick={() => { setPage(label); setSearch(''); if (label === 'Produkty' || label === 'Cenovky') void loadProducts(); if (label === STORES_PAGE_LABEL) { void loadStores(); void loadFiskalProShops() } if (label === STOCK_PAGE_LABEL) { if (!stores.length) void loadStores(); if (stores[0]) void loadLocalStock(stores[0].id) } }}>
                <NavIcon name={icon} />{label}
              </button>
            ))}
          </div>)}
        </nav>

        <div className="sidebar-bottom">

          <div className="avatar">E</div>

          <div><strong>Cukráreň Emili</strong><small>Administrácia</small></div>

        </div>

      </aside>



      <main className="main-area">

        <header className="topbar">

          <div><span className="crumb">Cukráreň Emili</span><span className="crumb-sep"> / </span>{page}</div>

          <div className="top-actions"><MaintenanceBadge refreshKey={maintenanceTick} onOpen={() => setPage(MAINTENANCE_PAGE)} /><span className="status-dot" /> Administrácia <button className="logout-btn" onClick={() => signOut()}>Odhlásiť</button></div>

        </header>



        <section className={`content ${page === 'Vzhľad e-shopu' ? 'is-wide' : ''}`}>

          <div className="page-heading">
            <div>
              <h1>{page}</h1>
              <p className="subtitle">{PAGE_INFO[page] ?? ''}</p>
            </div>
            {page === 'Produkty' && <div className="page-heading-actions">
              {copyPickMode && <button className="secondary-btn" onClick={() => setCopyPickMode(false)}>Zrušiť kopírovanie</button>}
              <button className={`secondary-btn ${copyPickMode ? 'is-active' : ''}`} onClick={() => setCopyPickMode(current => !current)}>⧉ Pridať kopírovaním</button>
              <button className="primary-btn" onClick={() => { setCopyPickMode(false); resetForm(); setShowForm(true) }}>＋ Pridať produkt</button>
            </div>}
          </div>

          {page === 'Produkty' && copyPickMode && <p className="copy-pick-banner">⧉ Kliknite v zozname na produkt, ktorý chcete skopírovať.</p>}

          {page === 'Štatistiky' && <StatsAdmin />}
          {page === 'Zákazníci' && <CustomersAdmin />}
          {page === 'Faktúry' && <InvoicesAdmin />}
          {(Object.keys(STOCK_PAGES) as StockView[]).map(v => page === STOCK_PAGES[v] && <StockAdmin key={v} view={v} onNavigate={next => setPage(STOCK_PAGES[next])} />)}
          {(Object.keys(SALES_PAGES) as SalesView[]).map(v => page === SALES_PAGES[v] && <SalesAdmin key={v} view={v} />)}
          {page === 'Tlačové zostavy' && <PrintSettingsAdmin />}
          {page === MAINTENANCE_PAGE && <MaintenanceAdmin onChanged={() => setMaintenanceTick(t => t + 1)} />}
          {page === 'Cenník dopravy' && <DeliveryRatesAdmin />}
          {page === 'Vzhľad e-shopu' && <SiteEditor />}
          {page === 'Kategórie' && <EshopCategories groupLabel={groupLabel} onProductsChanged={() => void loadProducts()} />}

          {page === 'Prehľad' && <DashboardAdmin onNavigate={navigateTo} />}



          {page === 'Objednávky' && <div className="panel orders-admin">
            <div className="pa-head">
              <div><h2>Objednávky</h2><p>{ordersTotal} {ordersTotal === 1 ? 'objednávka' : ordersTotal >= 2 && ordersTotal <= 4 ? 'objednávky' : 'objednávok'}{orderFiltersActive ? ' podľa filtrov' : ''}</p></div>
              <div className="pa-head-actions">
                <div className="pa-tabs" role="tablist" aria-label="Zobrazenie objednávok">
                  <button type="button" role="tab" aria-selected={ordersTab === 'list'} className={ordersTab === 'list' ? 'is-on' : ''} onClick={() => setOrdersTab('list')}>Zoznam</button>
                  <button type="button" role="tab" aria-selected={ordersTab === 'plan'} className={ordersTab === 'plan' ? 'is-on' : ''} onClick={() => setOrdersTab('plan')}>Výrobný plán</button>
                  <button type="button" role="tab" aria-selected={ordersTab === 'season'} className={ordersTab === 'season' ? 'is-on' : ''} onClick={() => setOrdersTab('season')}>🎄 Vianoce</button>
                </div>
                {ordersTab === 'list' && <button type="button" className="primary-btn" onClick={() => setNewOrderOpen(true)}>+ Nová objednávka</button>}
                {ordersTab !== 'season' && <button type="button" className="pa-ghost" onClick={() => void (ordersTab === 'plan' ? loadProductionPlan() : loadOrders())}>↻ Obnoviť</button>}
              </div>
            </div>

            {ordersTab === 'list' && <>
              <div className="or-toolbar">
                <label className="pa-search">
                  <span aria-hidden="true">⌕</span>
                  <input placeholder="Hľadať číslo objednávky alebo zákazníka…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void loadOrders(1) }} />
                  {search && <button type="button" aria-label="Vymazať hľadanie" onClick={() => setSearch('')}>×</button>}
                </label>
                <select aria-label="Filtrovať podľa predajne" className={orderStoreFilter ? 'is-set' : ''} value={orderStoreFilter} onChange={e => setOrderStoreFilter(e.target.value)}><option value="">Všetky prevádzky</option>{stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}</select>
                <select aria-label="Filtrovať podľa stavu objednávky" className={orderStatusFilter ? 'is-set' : ''} value={orderStatusFilter} onChange={e => setOrderStatusFilter(e.target.value)}><option value="">Všetky stavy</option>{ORDER_STATUSES.map(status => <option key={status.code} value={status.code}>{status.label}</option>)}</select>
                <select aria-label="Filtrovať podľa posúdenia termínu" className={scheduleStatusFilter ? 'is-set' : ''} value={scheduleStatusFilter} onChange={e => setScheduleStatusFilter(e.target.value)}><option value="">Termín: všetky</option>{SCHEDULE_STATUSES.map(status => <option key={status.code} value={status.code}>{status.label}</option>)}</select>
              </div>
              <div className="or-quick">
                <span className="pa-issues-label">Termín:</span>
                {orderDatePresets.map(preset => <button key={preset.label} type="button" className={`or-preset ${orderDateFrom === preset.from && orderDateTo === preset.to ? 'is-on' : ''}`} onClick={() => { setOrderDateFrom(preset.from); setOrderDateTo(preset.to) }}>{preset.label}</button>)}
                <DateField className="or-datefield" value={orderDateFrom} onChange={setOrderDateFrom} placeholder="Od dňa" max={orderDateTo || undefined} />
                <DateField className="or-datefield" value={orderDateTo} onChange={setOrderDateTo} placeholder="Do dňa" min={orderDateFrom || undefined} />
                <button type="button" className={`pa-chip ${seasonalOnly ? 'is-on' : ''}`} onClick={() => setSeasonalOnly(v => !v)}>🎄 Iba vianočné</button>
                <button type="button" className={`pa-chip ${scheduleStatusFilter === 'PENDING' ? 'is-on' : ''}`} onClick={() => setScheduleStatusFilter(scheduleStatusFilter === 'PENDING' ? '' : 'PENDING')}>Čaká na potvrdenie termínu</button>
                {orderFiltersActive && <button type="button" className="pa-link" onClick={clearOrderFilters}>Zrušiť filtre</button>}
                {orderDateFrom && orderDateFrom === orderDateTo && <button type="button" className="pa-ghost or-print-btn" onClick={() => openPrint(`/api/admin/orders-print?${new URLSearchParams({ day: orderDateFrom, ...(orderStoreFilter ? { storeId: orderStoreFilter } : {}), ...(seasonalOnly ? { seasonal: '1' } : {}), format: 'a5' })}`)}>🖨 Tlačiť deň</button>}
              </div>

              {error && <p className="error-message">{error}</p>}

              {loading && <p className="empty-message">Načítavam objednávky…</p>}

              {!loading && !error && <OrderTable orders={filteredOrders} onSelectOrder={openOrder} />}

              {!loading && !error && !filteredOrders.length && <div className="pa-empty"><span>♡</span><strong>{orderFiltersActive ? 'Žiadna objednávka nevyhovuje filtrom' : 'Zatiaľ tu nie sú žiadne objednávky'}</strong>{orderFiltersActive && <button type="button" className="pa-link" onClick={clearOrderFilters}>Zrušiť filtre</button>}</div>}
              {!loading && !error && ordersTotal > 50 && <div className="order-pagination"><span>Zobrazené {ordersTotal ? (ordersPage - 1) * 50 + 1 : 0} – {Math.min(ordersPage * 50, ordersTotal)} z {ordersTotal}</span><button disabled={ordersPage <= 1} onClick={() => void loadOrders(ordersPage - 1)}>‹ Predchádzajúca</button><button disabled={ordersPage * 50 >= ordersTotal} onClick={() => void loadOrders(ordersPage + 1)}>Ďalšia ›</button></div>}
            </>}

            {ordersTab === 'season' && <SeasonalOrdersSettings onShowOrders={() => { setSeasonalOnly(true); setOrdersTab('list') }} />}

            {ordersTab === 'plan' && <section className="production-plan-panel or-plan" aria-label="Denný výrobný prehľad">
              <div className="production-plan-heading">
                <div><h3>Denný výrobný prehľad</h3><p>Čo treba vyrobiť v daný deň – iba objednávky s potvrdeným termínom, rozdelené podľa prevádzky.</p></div>
                <DateField label="Deň" value={productionPlanDate} onChange={setProductionPlanDate} clearable={false} />
                <label>Prevádzka<select value={productionPlanStoreId} onChange={e => setProductionPlanStoreId(e.target.value)}><option value="">Všetky prevádzky</option>{stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
                <button className="primary-btn" onClick={() => void loadProductionPlan()} disabled={productionPlanLoading || !productionPlanDate}>{productionPlanLoading ? 'Načítavam…' : 'Načítať plán'}</button>
              </div>
              <div className="or-print-day">
                <div><strong>🖨 Tlač objednávok na deň</strong><span>Súhrn výroby + každá objednávka na samostatnej strane s kódom, QR a čiarovým kódom pre skener pri pokladni. Zahŕňa aj termíny čakajúce na potvrdenie.</span></div>
                <label className="seasonal-check"><input type="checkbox" checked={printSeasonalOnly} onChange={e => setPrintSeasonalOnly(e.target.checked)} /> Iba vianočné</label>
                <button type="button" className="pa-ghost" disabled={!productionPlanDate} onClick={() => openPrint(`/api/admin/orders-print?${new URLSearchParams({ day: productionPlanDate, ...(productionPlanStoreId ? { storeId: productionPlanStoreId } : {}), ...(printSeasonalOnly ? { seasonal: '1' } : {}), format: 'a5' })}`)}>Tlačiť A5</button>
                <button type="button" className="pa-ghost" disabled={!productionPlanDate} onClick={() => openPrint(`/api/admin/orders-print?${new URLSearchParams({ day: productionPlanDate, ...(productionPlanStoreId ? { storeId: productionPlanStoreId } : {}), ...(printSeasonalOnly ? { seasonal: '1' } : {}), format: 'a4' })}`)}>Tlačiť A4</button>
              </div>
              {productionPlanError && <p className="error-message" role="alert">{productionPlanError}</p>}
              {productionPlanLoading && <p className="empty-message">Načítavam potvrdené termíny…</p>}
              {productionPlan && <>
                <p className="production-plan-summary">{new Date(`${productionPlan.date}T12:00:00Z`).toLocaleDateString('sk-SK', { timeZone: 'Europe/Bratislava', dateStyle: 'full' })}: {productionPlan.orderCount} {productionPlan.orderCount === 1 ? 'objednávka' : productionPlan.orderCount >= 2 && productionPlan.orderCount <= 4 ? 'objednávky' : 'objednávok'} s potvrdeným termínom</p>
                {!productionPlan.orderCount && <p className="empty-message">Na tento deň nie sú potvrdené objednávky.</p>}
                {productionPlan.stores.map(store => <section className="production-store" key={store.storeId}>
                  <h4>{store.storeName} <span>{store.orderCount} {store.orderCount === 1 ? 'objednávka' : store.orderCount >= 2 && store.orderCount <= 4 ? 'objednávky' : 'objednávok'}</span></h4>
                  <div className="production-products"><strong>Súhrn na výrobu</strong>{store.products.map(product => <span key={product.productId}>{product.name}: <b>{product.quantity} {product.unit}</b></span>)}</div>
                  <div className="production-orders">{store.orders.map(order => <article key={order.id}>
                    <div><strong>{order.confirmedFulfillmentAt ? new Date(order.confirmedFulfillmentAt).toLocaleTimeString('sk-SK', { timeZone: 'Europe/Bratislava', hour: 'numeric', minute: '2-digit' }) : 'Bez času'}</strong><span>{order.orderNumber} · {orderStatus(order.status).label} · {paymentLabel(order)}</span></div>
                    <span>{order.items.map(item => `${item.quantity} ${item.unit} ${item.name}`).join(', ')}</span>
                  </article>)}</div>
                </section>)}
              </>}
            </section>}
          </div>}



          {page === 'Produkty' && <ProductsAdmin
            products={products}
            error={productError}
            shopCategories={shopCategoryOptions}
            groupLabel={groupLabel}
            onEdit={product => {
              const full = products.find(p => p.id === product.id)
              if (!full) return
              if (copyPickMode) { setCopyPickMode(false); copyProduct(full) } else editProduct(full)
            }}
            onReload={() => loadProducts()}
            onOpenGroups={() => setFiskalGroupsOpen(true)} />}

          {page === 'Cenovky' && <PriceTagsAdmin products={products} categories={shopCategoryOptions} />}



          {page === STOCK_PAGE_LABEL && <div className="panel stores-panel">
            <div className="panel-heading"><div><h2>Lokálny sklad e-shopu</h2><p>Lokálny sklad e-shopu – nesynchronizované s FiskalPRO.</p></div>
              <button className="text-btn" onClick={() => selectedStockStoreId && void loadLocalStock(selectedStockStoreId)} disabled={stockLoading}>Obnoviť</button></div>
            <p className="stores-info">Zobrazené sú údaje uložené v databáze e-shopu. Záporné hodnoty zostávajú nezmenené.</p>
            <label className="store-field">Predajňa
              <select value={selectedStockStoreId} onChange={(event) => void loadLocalStock(event.target.value)}>
                <option value="">Vyber predajňu</option>{stores.map((store) => <option key={store.id} value={store.id}>{store.name} · {store.id}</option>)}
              </select>
            </label>
            {stockError && <p className="error-message" role="alert">{stockError}</p>}
            {stockLoading ? <p className="empty-message">Načítavam lokálny sklad…</p> : selectedStockStoreId ? <>
              <div className="panel stock-subpanel"><div className="panel-heading"><div><h3>Zostatky produktov</h3><p>Chýbajúci záznam sa zobrazuje ako „Neevidovaný“.</p></div></div>
                <label className="store-field stock-search-field">Vyhľadať produkt podľa názvu alebo kódu
                  <input type="search" value={stockProductSearch} onChange={(event) => setStockProductSearch(event.target.value)} placeholder="Napr. DEZERT - Čokoláda s malinami alebo kód 1" />
                </label>
                <p className="stock-result-count" aria-live="polite">Zobrazené {filteredStockProducts.length} z {products.filter((product) => product.active).length} aktívnych produktov</p>
                <div className="table-wrap"><table><thead><tr><th>PRODUKT</th><th>ZOSTATOK</th><th>POSLEDNÁ ZMENA</th></tr></thead><tbody>
                  {filteredStockProducts.map((product) => {
                    const balance = stockBalances.find((item) => item.productId === product.id)
                    const quantity = balance ? Number(balance.quantity).toLocaleString("sk-SK", { maximumFractionDigits: 3 }) + " " + product.unit : "Neevidovaný"
                    return <tr key={product.id}><td>{product.name}</td><td className={balance && Number(balance.quantity) < 0 ? "stock-negative" : ""}>{quantity}</td><td>{balance ? new Date(balance.updatedAt).toLocaleString("sk-SK") : "—"}</td></tr>
                  })}
                  {!filteredStockProducts.length && <tr><td colSpan={3}>{stockProductSearch ? "Nenašli sa produkty zodpovedajúce hľadaniu." : "Zatiaľ nie sú načítané aktívne produkty."}</td></tr>}
                </tbody></table></div>
              </div>
              <div className="panel stock-subpanel"><div className="panel-heading"><div><h3>Skladové pohyby</h3><p>Najnovšie pohyby sú zobrazené ako prvé.</p></div></div>
                {stockMovements.length ? <div className="table-wrap"><table><thead><tr><th>DÁTUM</th><th>PRODUKT</th><th>TYP</th><th>MNOŽSTVO</th><th>POZNÁMKA / REFERENCIA</th></tr></thead><tbody>
                  {stockMovements.map((movement) => <tr key={movement.id}><td>{new Date(movement.createdAt).toLocaleString("sk-SK")}</td><td>{movement.product.name}</td><td>{movement.type}</td><td className={Number(movement.quantity) < 0 ? "stock-negative" : ""}>{Number(movement.quantity).toLocaleString("sk-SK", { maximumFractionDigits: 3 })} {movement.product.unit}</td><td>{[movement.note, movement.reference].filter(Boolean).join(" · ") || "—"}</td></tr>)}
                </tbody></table></div> : <p className="empty-message">Pre túto predajňu nie sú evidované skladové pohyby.</p>}
              </div>
            </> : <p className="empty-message">Vyber predajňu. Chýbajúce záznamy nebudú nahradené vymyslenými nulami.</p>}
          </div>}

          {page === STORES_PAGE_LABEL && <StoresAdmin
            stores={stores}
            drafts={storeDrafts}
            setDrafts={setStoreDrafts}
            shops={fiskalProShops}
            loading={storeLoading}
            shopsLoading={fiskalProShopsLoading}
            savingId={savingStoreId}
            error={storeError}
            onSave={storeId => saveStore(storeId)}
            onReload={() => loadStores()}
            onLoadShops={() => loadFiskalProShops()}
            onHoursSaved={(storeId, openingHours) => setStores(current => current.map(store => store.id === storeId ? { ...store, openingHours } : store))}
            onReorder={async ids => {
              const previous = stores
              setStores(ids.map(id => stores.find(store => store.id === id)!).filter(Boolean))
              try {
                const response = await adminFetch(`${API}/api/admin/stores/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) })
                if (!response.ok) throw new Error((await response.json().catch(() => null))?.error || 'Poradie sa nepodarilo uložiť.')
              } catch (e) { setStores(previous); setStoreError(e instanceof Error ? e.message : 'Poradie sa nepodarilo uložiť.') }
            }} />}

          {page !== 'Prehľad' && page !== 'Objednávky' && page !== 'Štatistiky' && page !== 'Produkty' && page !== 'Nastavenia' && page !== 'Kategórie' && page !== 'Cenovky' && page !== 'Zákazníci' && page !== 'Faktúry' && page !== STORES_PAGE_LABEL && page !== STOCK_PAGE_LABEL && page !== 'Cenník dopravy' && page !== 'Vzhľad e-shopu' && !Object.values(STOCK_PAGES).includes(page) && !Object.values(SALES_PAGES).includes(page) && page !== MAINTENANCE_PAGE && page !== 'Tlačové zostavy' &&

            <div className="panel placeholder-panel"><div className="placeholder-icon">✧</div><h2>{page}</h2><p>Túto časť doplníme v ďalšom kroku.</p></div>}

        </section>

        {newOrderOpen && (newOrderProducts
          ? <NewOrderAdmin stores={stores} products={newOrderProducts} categories={newOrderCategories} onClose={() => setNewOrderOpen(false)}
              onCreated={() => { setNewOrderOpen(false); void loadOrders() }} />
          : <div className="modal-backdrop" onClick={() => setNewOrderOpen(false)}><section className="order-detail new-order-panel" onClick={e => e.stopPropagation()}><p className="empty-message">Načítavam produkty…</p></section></div>)}

        {selectedOrder && <div className="modal-backdrop" onClick={() => setSelectedOrder(null)}>
          <section className="order-detail" role="dialog" aria-modal="true" aria-labelledby="order-detail-title" onClick={(event) => event.stopPropagation()}>
            <div className="panel-heading">
              <div><p>Detail objednávky{selectedOrder.seasonalRuleId ? <span className="or-season-badge">🎄 Vianočná objednávka</span> : null}</p><h2 id="order-detail-title">{selectedOrder.orderNumber}</h2></div>
              <div className="order-print-actions">
                <button type="button" className="pa-ghost" title="Tlač pre výrobu a predajňu – s QR a čiarovým kódom" onClick={() => openPrint(`/api/admin/orders-print?ids=${encodeURIComponent(selectedOrder.id)}&format=a5`)}>🖨 Náhľad a tlač A5</button>
                <button type="button" className="pa-ghost" onClick={() => openPrint(`/api/admin/orders-print?ids=${encodeURIComponent(selectedOrder.id)}&format=a4`)}>A4</button>
                <button type="button" className="close-btn" aria-label="Zavrieť detail objednávky" onClick={() => setSelectedOrder(null)}>×</button>
              </div>
            </div>
            <dl className="order-detail-summary">
              <div><dt>Stav</dt><dd><span className={`or-pill is-strong tone-${orderStatus(selectedOrder.status).tone}`}>{orderStatus(selectedOrder.status).label}</span></dd></div>
              <div><dt>Platba</dt><dd>{paymentLabel(selectedOrder)}{selectedOrder.paidAt ? ` · ${new Date(selectedOrder.paidAt).toLocaleString('sk-SK')}` : ''}{selectedOrder.refundedCents ? <><br />Vrátené: {euro(selectedOrder.refundedCents)}</> : null}</dd></div>
              {selectedOrder.invoices?.length ? <div><dt>Doklady</dt><dd>{selectedOrder.invoices.map(invoice => <button type="button" key={invoice.id} className="text-btn" onClick={() => openPrint(`/api/admin/invoices/${invoice.id}/html`, `${invoice.kind === 'CREDIT_NOTE' ? 'Dobropis' : 'Faktúra'} ${invoice.number}`)}>{invoice.kind === 'CREDIT_NOTE' ? 'Dobropis' : 'Faktúra'} {invoice.number}</button>)}</dd></div> : null}
              <div><dt>Doručenie</dt><dd>{DELIVERY_LABEL[selectedOrder.deliveryMethod ?? 'PICKUP'] ?? selectedOrder.deliveryMethod}{selectedOrder.deliveryPricePending ? ' · treba naceniť dopravu' : ''}</dd></div>
              {selectedOrder.store?.name && <div><dt>Prevádzka</dt><dd>{selectedOrder.store.name}</dd></div>}
              {selectedOrder.createdAt && <div><dt>Vytvorená</dt><dd>{new Date(selectedOrder.createdAt).toLocaleString('sk-SK')}</dd></div>}
              {selectedOrder.discountCents ? <div><dt>Zľava zákazníka</dt><dd>−{euro(selectedOrder.discountCents)} ({selectedOrder.discountPercent} %)</dd></div> : null}
              <div><dt>Celková suma</dt><dd><strong>{euro(selectedOrder.totalCents)}</strong></dd></div>
              <div><dt>Zákazník</dt><dd>{selectedOrder.customerId ? 'Registrovaný zákazník' : 'Nákup bez registrácie'}</dd></div>
              {selectedOrder.companyName && <div><dt>Firma (doklad)</dt><dd>{selectedOrder.companyName}<br />IČO {selectedOrder.companyIco}{selectedOrder.companyDic ? `, DIČ ${selectedOrder.companyDic}` : ''}{selectedOrder.companyIcDph ? `, IČ DPH ${selectedOrder.companyIcDph}` : ''}{selectedOrder.billingAddress ? <><br />{selectedOrder.billingAddress}</> : null}</dd></div>}
            </dl>
            {selectedOrder.paymentMethod === 'CARD_ONLINE' && <section className="payment-workflow" aria-label="Online platba">
              <h3>Online platba (Comgate)</h3>
              {selectedOrder.paymentStatus === 'PAID' && ['CANCELLED', 'REJECTED'].includes(selectedOrder.status) && (selectedOrder.refundedCents ?? 0) < selectedOrder.totalCents && <p className="error-message">Objednávka je zrušená, ale zaplatená – vráťte zákazníkovi peniaze.</p>}
              {paymentError && <p className="error-message" role="alert">{paymentError}</p>}
              <div className="workflow-actions">
                {selectedOrder.paymentStatus !== 'PAID' && <button disabled={paymentBusy} onClick={() => void syncPayment()}>Overiť stav platby</button>}
                {selectedOrder.paymentStatus === 'PAID' && (selectedOrder.refundedCents ?? 0) < selectedOrder.totalCents && <>
                  <label>Suma na vrátenie (€)<input type="number" min="0.01" step="0.01" value={refundAmount} placeholder={((selectedOrder.totalCents - (selectedOrder.refundedCents ?? 0)) / 100).toFixed(2)} onChange={e => setRefundAmount(e.target.value)} /></label>
                  <button disabled={paymentBusy} onClick={() => void refundPayment()}>Vrátiť platbu</button>
                </>}
              </div>
            </section>}
            <OrderDelivery order={selectedOrder} stores={stores} onUpdated={patch => { setSelectedOrder(current => current ? { ...current, ...patch } : current); setOrders(current => current.map(o => o.id === selectedOrder.id ? { ...o, ...patch } : o)) }} />
            <section className="schedule-workflow" aria-label="Posúdenie termínu">
              <h3>Termín vybavenia</h3>
              <p>Stav posúdenia: <strong>{scheduleStatus(selectedOrder.scheduleStatus).label}</strong></p>
              <p>Požadovaný termín: {selectedOrder.requestedFulfillmentAt ? termLabel(selectedOrder.requestedFulfillmentAt) : 'zákazník nevybral – nastavte ho nižšie'}</p>
              {selectedOrder.proposedFulfillmentAt && <p>Navrhnutý termín: {termLabel(selectedOrder.proposedFulfillmentAt)}</p>}
              {selectedOrder.confirmedFulfillmentAt && <p>Potvrdený termín: <strong>{termLabel(selectedOrder.confirmedFulfillmentAt)}</strong></p>}
              {workflowError && <p className="error-message" role="alert">{workflowError}</p>}
              {['NEW', 'CONFIRMED'].includes(selectedOrder.status) && (['PENDING', 'ALTERNATIVE_PROPOSED'].includes(selectedOrder.scheduleStatus ?? '') || (!selectedOrder.scheduleStatus && !selectedOrder.requestedFulfillmentAt)) && <div className="workflow-actions">
                {selectedOrder.requestedFulfillmentAt && selectedOrder.scheduleStatus === 'PENDING' && <button className="primary-btn" onClick={() => void runOrderWorkflowAction('schedule/confirm')}>Potvrdiť termín {termLabel(selectedOrder.requestedFulfillmentAt)}</button>}
                <DateTimeField allowDayOnly label={selectedOrder.requestedFulfillmentAt ? 'Navrhnúť iný termín' : 'Termín prevzatia (čas nepovinný)'} value={alternativeDate} onChange={setAlternativeDate} />
                <button disabled={!termInputValid(alternativeDate)} onClick={() => void runOrderWorkflowAction('schedule/propose', { proposedFulfillmentAt: termInputIso(alternativeDate) })}>{!selectedOrder.requestedFulfillmentAt ? 'Potvrdiť termín' : selectedOrder.scheduleStatus === 'ALTERNATIVE_PROPOSED' ? 'Zmeniť navrhnutý termín' : 'Navrhnúť termín'}</button>
                <button onClick={() => { const reason = window.prompt('Dôvod odmietnutia objednávky:'); if (reason?.trim()) void runOrderWorkflowAction('schedule/reject', { reason: reason.trim() }) }}>Odmietnuť</button>
              </div>}
              {['NEW', 'CONFIRMED'].includes(selectedOrder.status) && selectedOrder.scheduleStatus === 'ALTERNATIVE_PROPOSED' && <div className="term-wait">
                <p><strong>Čaká sa na odpoveď zákazníka.</strong> {selectedOrder.customerId
                  ? 'Zákazník má účet – návrh vidí vo svojom účte a môže ho potvrdiť sám.'
                  : 'Zákazník nemá účet – dohodnite sa s ním telefonicky.'} Ak vám súhlas dal telefonicky alebo osobne, potvrďte termín tu.</p>
                <button onClick={() => { if (window.confirm(`Zákazník súhlasí s termínom ${selectedOrder.proposedFulfillmentAt ? new Date(selectedOrder.proposedFulfillmentAt).toLocaleString('sk-SK', { timeZone: 'Europe/Bratislava' }) : ''}? Termín sa potvrdí.`)) void runOrderWorkflowAction('schedule/accept-proposed') }}>Zákazník súhlasí – potvrdiť termín</button>
              </div>}
              <div className="workflow-actions">
                {selectedOrder.status === 'NEW' && <button onClick={() => void changeOrderStatus('CONFIRMED')}>Potvrdiť objednávku</button>}
                {selectedOrder.status === 'CONFIRMED' && <button onClick={() => void changeOrderStatus('IN_PRODUCTION')}>Začať výrobu</button>}
                {selectedOrder.status === 'IN_PRODUCTION' && <button onClick={() => void changeOrderStatus('READY')}>Označiť pripravenú</button>}
                {selectedOrder.status === 'READY' && <button onClick={() => void changeOrderStatus(selectedOrder.deliveryMethod !== 'PICKUP' ? 'DELIVERED' : 'PICKED_UP')}>{selectedOrder.deliveryMethod === 'SHIPPING' ? 'Označiť odoslanú' : selectedOrder.deliveryMethod === 'COURIER' ? 'Označiť doručenú' : 'Označiť vyzdvihnutú'}</button>}
                {['NEW','CONFIRMED'].includes(selectedOrder.status) && <button onClick={() => { const reason = window.prompt('Dôvod zrušenia objednávky:'); if (reason?.trim()) void runOrderWorkflowAction('cancel', { reason: reason.trim() }) }}>Zrušiť objednávku</button>}
              </div>
            </section>
            <div className="fiskalpro-send-controls" aria-live="polite">
              {selectedOrder.fiskalproStatus || selectedOrder.fiskalproOrderId ? (
                <p className="hint">
                  {selectedOrder.fiskalproStatus === "SYNCED"
                    ? "FiskalPRO potvrdilo import odloženého dokladu. Samostatné ID odpoveď neposkytla; doklad hľadaj podľa čísla objednávky."
                    : "FiskalPRO stav: " + (selectedOrder.fiskalproStatus || "odoslané") + ". Ďalšie odoslanie je zablokované; pri stave UNKNOWN najprv over doklad vo FiskalPRO."}
                </p>
              ) : (
                <button type="button" className="primary-btn" disabled={fiskalProSendLoading} onClick={() => void sendOrderToFiskalPro(selectedOrder)}>
                  {fiskalProSendLoading ? "Odosielam…" : "Odoslať do FiskalPRO ako odložený doklad"}
                </button>
              )}
              {!selectedOrder.fiskalproStatus && !selectedOrder.fiskalproOrderId && <p className="hint">Cieľ: Kanianka, shop 73633, pokladnica C9, POS 113148.</p>}
              {fiskalProSendError && <p className="error-message" role="alert">{fiskalProSendError}</p>}
              <p className="hint">Akcia odošle iba túto objednávku. Odložený doklad musí obsluha skontrolovať a dokončiť samostatne na pokladnici.</p>
            </div>
            <div className="od-items-head"><h3>Položky</h3>
              {itemVatEditable && (selectedOrder.items?.length ?? 0) > 1 && <div className="vat-all"><span>Všetko:</span>
                <button type="button" disabled={vatBusy} onClick={() => void setItemsVat(selectedOrder.items!.map(i => ({ id: i.id, onSite: false })))}>🥡 So sebou</button>
                <button type="button" disabled={vatBusy} onClick={() => void setItemsVat(selectedOrder.items!.map(i => ({ id: i.id, onSite: true })))}>🍽 Na mieste</button>
              </div>}
            </div>
            {vatError && <p className="error-message" role="alert">{vatError}</p>}
            {selectedOrder.items?.length ? <div className="table-wrap"><table className="od-items">
              <thead><tr><th>PRODUKT</th><th>MNOŽSTVO</th><th>CENA / KS</th><th title="🥡 so sebou · 🍽 na mieste">DPH 🥡 / 🍽</th><th>SPOLU</th></tr></thead>
              <tbody>{selectedOrder.items.map((item) => {
                const away = item.product.vatRate ?? item.product.fiskalproVatRate ?? null
                const onSite = item.product.vatRateOnSite ?? away
                return <tr key={item.id}>
                  <td>{item.product.name}{item.variantName ? ` – ${item.variantName}` : ''}</td>
                  <td>{Number(item.quantity).toLocaleString('sk-SK', { maximumFractionDigits: 3 })} {item.product.unit}</td>
                  <td>{euro(item.unitPriceCents)}</td>
                  <td><div className="vat-switch" role="radiogroup" aria-label={`DPH – ${item.product.name}`}>
                    <button type="button" role="radio" aria-checked={!item.vatOnSite} className={!item.vatOnSite ? 'is-on' : ''} disabled={!itemVatEditable || vatBusy} onClick={() => item.vatOnSite && void setItemsVat([{ id: item.id, onSite: false }])} title={`So sebou (odnos)${away !== null ? ` – DPH ${away} %` : ''}`}>🥡 {away !== null ? `${away} %` : 'So sebou'}</button>
                    <button type="button" role="radio" aria-checked={Boolean(item.vatOnSite)} className={item.vatOnSite ? 'is-on' : ''} disabled={!itemVatEditable || vatBusy} onClick={() => !item.vatOnSite && void setItemsVat([{ id: item.id, onSite: true }])} title={`Na mieste (konzumácia v predajni)${onSite !== null ? ` – DPH ${onSite} %` : ''}`}>🍽 {onSite !== null ? `${onSite} %` : 'Na mieste'}</button>
                  </div></td>
                  <td className="amount">{euro(item.lineTotalCents)}</td>
                </tr>
              })}</tbody>
            </table></div> : <p className="empty-message">Objednávka neobsahuje položky.</p>}
            {!itemVatEditable && selectedOrder.invoices?.some(i => i.kind === 'INVOICE') && <p className="hint">K objednávke je vystavená faktúra – DPH položiek sa už nemení (opravuje sa dobropisom).</p>}
            {vatWarning && <p className="hint">{vatWarning}</p>}

            <section className="od-print-preview">
              <div className="od-items-head"><h3>Náhľad tlače</h3>
                <div className="vat-all">
                  <button type="button" className={previewFormat === 'a5' ? 'is-on' : ''} onClick={() => setPreviewFormat('a5')}>A5</button>
                  <button type="button" className={previewFormat === 'a4' ? 'is-on' : ''} onClick={() => setPreviewFormat('a4')}>A4</button>
                  <button type="button" onClick={() => openPrint(`/api/admin/orders-print?ids=${encodeURIComponent(selectedOrder.id)}&format=${previewFormat}`)}>🖨 Tlačiť</button>
                </div>
              </div>
              <PrintPreviewFrame url={`${API}/api/admin/orders-print?ids=${encodeURIComponent(selectedOrder.id)}&format=${previewFormat}`} refreshKey={JSON.stringify([selectedOrder.status, selectedOrder.scheduleStatus, selectedOrder.storeId, selectedOrder.deliveryMethod, selectedOrder.orderNumber, selectedOrder.items?.map(i => i.vatOnSite)])} height={previewFormat === 'a5' ? 640 : 820} />
            </section>
          </section>
        </div>}

      </main>



      {fiskalGroupsOpen && <FiskalGroupsDialog groups={fiskalGroupList} info={fiskalGroupsInfo} onClose={() => setFiskalGroupsOpen(false)}
        onChanged={(groups, moved, info) => { setFiskalGroupList(groups); if (info) setFiskalGroupsInfo(info); if (moved) void loadProducts() }} />}
      {showForm && <div className="modal-backdrop" onClick={resetForm}>

        <form className="product-form" onSubmit={saveProduct} onClick={e => e.stopPropagation()}>

          <div className="panel-heading"><div><h2>{editingId ? 'Upraviť produkt' : 'Pridať produkt'}</h2><p>Vyplň údaje výrobku</p></div>

            <button type="button" className="close-btn" onClick={resetForm}>✕</button></div>

          <label>Názov výrobku *<input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>

          <div className="form-row">

            {editingFromFiskal
              ? <label>Skupina z pokladne (FiskalPRO)<input className="readonly-field" readOnly value={groupLabel(form.category) === form.category ? form.category : `${groupLabel(form.category)} (skupina ${form.category.replace(/\D+/g, '')})`} title="Skupina sa mení iba v pokladni FiskalPRO" /><small className="field-note">🔒 Mení sa iba v pokladni FiskalPRO</small></label>
              : <label>Skupina z pokladne (FiskalPRO) *<select required value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="">Vyberte skupinu…</option>
                  {form.category && !fiskalGroups.includes(form.category) && <option value={form.category}>{form.category} (mimo pokladne)</option>}
                  {fiskalGroups.map(group => <option key={group} value={group}>{groupLabel(group) === group ? group : `${groupLabel(group)} (skupina ${group.replace(/\D+/g, '')})`}</option>)}
                </select></label>}

            <label>Jednotka predaja<select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
              {UNIT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              {!UNIT_OPTIONS.some(option => option.value === form.unit) && form.unit && <option value={form.unit}>{form.unit} (z pokladne)</option>}
            </select></label>

          </div>
          <ShopCategoryPicker categories={shopCategoryOptions} value={form.shopCategoryIds} warn={form.eshopVisible && !form.shopCategoryIds.length}
            onChange={ids => setForm(current => ({ ...current, shopCategoryIds: ids }))} />

          <div className="form-row">

            <label>Predajná cena (€) *<input required type="number" min="0" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></label>

            <label>Nákladová cena (€)<input type="number" min="0" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></label>

          </div>

          <div className="form-row">

            <label>PLU z FiskalPRO<input value={form.plu} onChange={e => setForm({ ...form, plu: e.target.value })} /></label>

            <label>SKU / interný kód<input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} /></label>

          </div>

          <label>Opis<textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} /></label>
          <div className={`form-row ${form.measureKind !== 'none' ? 'three' : ''}`}>
            <label>Údaj o množstve
              <select aria-label="Hmotnosť alebo objem" value={form.measureKind} onChange={e => setForm({ ...form, measureKind: e.target.value as MeasureKind })}>
                <option value="none">Neuvádzať</option>
                <option value="weight">Hmotnosť</option>
                <option value="volume">Objem</option>
              </select>
            </label>
            {form.measureKind === 'weight' && <label>Hmotnosť{form.variants.length > 0 && <small> – pri variantoch zadaj hmotnosť pri variante</small>}
              <span className="input-with-unit">
                <input type="text" inputMode="decimal" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} placeholder={form.weightUnit === 'kg' ? 'napr. 1,5' : 'napr. 120'} />
                <select aria-label="Jednotka hmotnosti" value={form.weightUnit} onChange={e => {
                  // Číslo ostáva, ako bolo zadané – mení sa iba jednotka (zadám „1,2“ a vyberiem kg).
                  setForm({ ...form, weightUnit: e.target.value as WeightUnit })
                }}><option value="g">g</option><option value="kg">kg</option></select>
              </span>
            </label>}
            {form.measureKind === 'volume' && <label>Objem{form.variants.length > 0 && <small> – pri variantoch zadaj objem pri variante</small>}
              <span className="input-with-unit">
                <input type="text" inputMode="decimal" value={form.volume} onChange={e => setForm({ ...form, volume: e.target.value })} placeholder={form.volumeUnit === 'l' ? 'napr. 1,5' : 'napr. 330'} />
                <select aria-label="Jednotka objemu" value={form.volumeUnit} onChange={e => {
                  // Číslo ostáva, ako bolo zadané – mení sa iba jednotka (zadám „1,2“ a vyberiem l).
                  setForm({ ...form, volumeUnit: e.target.value as VolumeUnit })
                }}><option value="ml">ml</option><option value="l">l</option></select>
              </span>
            </label>}
            <label>Trvanlivosť
              <span className="input-with-unit">
                <input type="number" min="1" max="9999" step="1" value={form.shelfValue} onChange={e => setForm({ ...form, shelfValue: e.target.value })} placeholder="napr. 3" />
                <select aria-label="Jednotka trvanlivosti" value={form.shelfUnit} onChange={e => setForm({ ...form, shelfUnit: e.target.value as ShelfUnit })}>
                  <option value="DAY">{shelfUnitWord(Number(form.shelfValue) || 5, 'DAY')}</option>
                  <option value="MONTH">{shelfUnitWord(Number(form.shelfValue) || 5, 'MONTH')}</option>
                </select>
              </span>
              <input maxLength={150} value={form.shelfNote} onChange={e => setForm({ ...form, shelfNote: e.target.value })} placeholder="Skladovanie (nepovinné), napr. pri 2–6 °C" />
            </label>
          </div>
          <div className="form-row three">
            <label>Minimálny odber v e-shope (ks)<input type="number" min="1" max="999" step="1" value={form.minOrder} onChange={e => setForm({ ...form, minOrder: e.target.value })} /></label>
            <label>DPH so sebou <small>(e-shop, odnos)</small><select value={form.vatRate} onChange={e => setForm({ ...form, vatRate: e.target.value })}>
              <option value="">{editingProductVat === null ? 'Podľa FiskalPRO (nezadaná)' : `Podľa FiskalPRO (${editingProductVat} %)`}</option>
              <option value="23">23 %</option><option value="19">19 %</option><option value="5">5 %</option><option value="0">0 %</option>
            </select>{form.vatRate !== '' && editingProductVat !== null && Number(form.vatRate) !== editingProductVat && <small className="form-warning">Pokladňa FiskalPRO má pri tomto artikli {editingProductVat} %. Doklad v pokladni sa vystaví podľa FiskalPRO – zosúlaď sadzbu aj tam.</small>}</label>
            <label>DPH na mieste <small>(konzumácia v predajni)</small><select value={form.vatRateOnSite} onChange={e => setForm({ ...form, vatRateOnSite: e.target.value })}>
              <option value="">Rovnaká ako so sebou</option>
              <option value="23">23 %</option><option value="19">19 %</option><option value="5">5 %</option><option value="0">0 %</option>
            </select><small className="field-hint">Použije sa na dokladoch so zaškrtnutým „Predaj na mieste“.</small></label>
          </div>
          <fieldset className="delivery-picker">
            <legend>Spôsob doručenia <small>– čo zákazník môže zvoliť v košíku</small></legend>
            <div className="delivery-grid">
              {DELIVERY_OPTIONS.map(option => <label key={option.key} className={`delivery-chip ${form[option.key] ? 'is-on' : ''}`}>
                <input type="checkbox" checked={form[option.key]} onChange={e => setForm({ ...form, [option.key]: e.target.checked })} />
                <span aria-hidden="true">{option.icon}</span><span><strong>{option.label}</strong><small>{option.hint}</small></span>
              </label>)}
            </div>
            {!form.allowPickup && !form.allowLocalDelivery && !form.allowShipping && <small className="form-warning">Vyberte aspoň jeden spôsob – inak sa produkt nedá objednať.</small>}
          </fieldset>
          <fieldset className="variant-editor">
            <legend>Varianty (napr. veľkosť torty) {form.variants.length > 0 && <small>– zákazník si musí vybrať jeden variant; cena produktu sa vtedy nepoužije</small>}</legend>
            {form.variants.length > 0 && <div className="variant-row variant-head" aria-hidden="true"><span>Názov variantu</span><span>Cena (€)</span><span>{form.measureKind === 'volume' ? `Objem (${form.volumeUnit})` : `Hmotnosť (${form.weightUnit})`}</span></div>}
            {form.variants.map((variant, index) => <div className="variant-row" key={variant.id ?? `new-${index}`} {...variantDrag.itemProps(String(index))}>
              <span {...variantDrag.handleProps(String(index))}>⋮⋮</span>
              <input aria-label="Názov variantu" placeholder="Názov, napr. 12 porcií" maxLength={60} value={variant.name} onChange={e => updateVariant(index, { name: e.target.value })} required />
              <input aria-label="Cena variantu v €" type="number" min="0" step="0.01" placeholder="Cena €" value={variant.price} onChange={e => updateVariant(index, { price: e.target.value })} required />
              {form.measureKind === 'volume'
                ? <input aria-label={`Objem variantu v ${form.volumeUnit}`} type="text" inputMode="decimal" placeholder={form.volumeUnit} value={variant.volume} onChange={e => updateVariant(index, { volume: e.target.value })} />
                : <input aria-label={`Hmotnosť variantu v ${form.weightUnit}`} type="text" inputMode="decimal" placeholder={form.weightUnit} disabled={form.measureKind === 'none'} value={variant.weight} onChange={e => updateVariant(index, { weight: e.target.value })} />}
              <input aria-label="Kód artikla FiskalPRO" placeholder="Kód FiskalPRO (nepovinný)" maxLength={40} value={variant.code} onChange={e => updateVariant(index, { code: e.target.value })} />
              <label className="check-label"><input type="checkbox" checked={variant.active} onChange={e => updateVariant(index, { active: e.target.checked })} /> V ponuke</label>
              <div className="variant-actions">
                <button type="button" aria-label="Odstrániť variant" className="danger" onClick={() => setForm({ ...form, variants: form.variants.filter((_, i) => i !== index) })}>✕</button>
              </div>
            </div>)}
            <button type="button" className="text-btn" onClick={() => setForm({ ...form, variants: [...form.variants, { name: '', price: form.price, weight: '', volume: '', code: '', active: true }] })}>＋ Pridať variant</button>
          </fieldset>
          <fieldset className="b2b-editor">
            <legend>Veľkoobchodná cena B2B <small>– pre odberateľov s nastavením „B2B ceny“ (karta odberateľa)</small></legend>
            <div className="form-row three">
              <label>Spôsob
                <select value={form.b2bMode} onChange={e => setForm({ ...form, b2bMode: e.target.value as B2bPricingMode })}>
                  <option value="NONE">Nemá B2B cenu</option>
                  <option value="DISCOUNT">Zľava z maloobchodnej ceny</option>
                  <option value="FIXED">Pevná cena (zadaná bez DPH)</option>
                </select>
              </label>
              {form.b2bMode === 'DISCOUNT' && <label>Zľava (%)<input type="number" min="0" max="100" step="1" value={form.b2bDiscount} onChange={e => setForm({ ...form, b2bDiscount: e.target.value })} /></label>}
              {form.b2bMode === 'FIXED' && <label>Pevná cena bez DPH (€)<input type="number" min="0" step="0.01" value={form.b2bFixed} onChange={e => setForm({ ...form, b2bFixed: e.target.value })} /></label>}
            </div>
            {form.b2bMode !== 'NONE' && <p className="form-hint">
              {b2bPreview
                ? <>B2B cena: <strong>{euro(b2bPreview.exclVatCents)}</strong> bez DPH · <strong>{euro(b2bPreview.inclVatCents)}</strong> s DPH{b2bPreviewVat !== null ? ` (DPH ${b2bPreviewVat} %)` : ' (DPH nezadaná – dopočíta sa pri uložení podľa FiskalPRO alebo 23 %)'}.</>
                : 'Zadajte hodnotu, podľa ktorej sa B2B cena dopočíta.'}
            </p>}
          </fieldset>
          <fieldset className="bundle-editor">
            <legend>Komplet <small>– produkt sa skladá z viacerých iných produktov (napr. Banketová misa = 30× Banketka)</small></legend>
            <label className="check-label"><input type="checkbox" checked={form.isBundle} onChange={e => setForm({ ...form, isBundle: e.target.checked, bundleItems: e.target.checked ? form.bundleItems : [] })} /> Toto je komplet</label>
            {form.isBundle && <>
              <p className="form-hint">Cena a sklad komplet-u sa zadávajú ručne vyššie. Pri predaji sa zo skladu odpočítajú zložky nižšie (nie komplet samotný).</p>
              <div className="bundle-filter">
                <input aria-label="Hľadať zložku" type="search" placeholder="🔍 Hľadať produkt…" value={bundleFilter.search} onChange={e => setBundleFilter({ ...bundleFilter, search: e.target.value })} />
                <select aria-label="Filter podľa kategórie" value={bundleFilter.category} onChange={e => setBundleFilter({ ...bundleFilter, category: e.target.value })}>
                  <option value="">Všetky kategórie</option>
                  {shopCategoryOptions.map(c => <option key={c.id} value={c.id}>{c.parentId ? `– ${c.name}` : c.name}</option>)}
                </select>
              </div>
              {form.bundleItems.length > 0 && <div className="bundle-row bundle-head" aria-hidden="true"><span>Zložka (produkt)</span><span>Počet na 1 komplet</span></div>}
              {form.bundleItems.map((item, index) => {
                const selected = bundleAllOptions.find(p => p.id === item.componentProductId)
                const rowOptions = selected && !bundleComponentOptions.some(p => p.id === selected.id) ? [selected, ...bundleComponentOptions] : bundleComponentOptions
                const unit = selected ? normalizeUnit(selected.unit) : 'ks'
                const wholeOnly = unit === 'ks'
                return <div className="bundle-row" key={index}>
                <select aria-label="Zložka komplet-u" value={item.componentProductId} onChange={e => updateBundleItem(index, { componentProductId: e.target.value })} required>
                  <option value="" disabled>– vyber produkt –</option>
                  {rowOptions.map(p => <option key={p.id} value={p.id} disabled={form.bundleItems.some((b, i) => i !== index && b.componentProductId === p.id)}>{p.name}</option>)}
                </select>
                <span className="bundle-qty">
                  <input aria-label="Počet kusov zložky" type="number" min={wholeOnly ? '1' : '0.01'} step={wholeOnly ? '1' : '0.01'} placeholder="Počet" value={item.quantity}
                    onChange={e => updateBundleItem(index, { quantity: wholeOnly ? e.target.value.replace(/[.,].*$/, '') : e.target.value })}
                    onBlur={() => wholeOnly && item.quantity && updateBundleItem(index, { quantity: String(Math.max(1, Math.round(Number(item.quantity.replace(',', '.')) || 1))) })} required />
                  <small>{unit}</small>
                </span>
                <button type="button" aria-label="Odstrániť zložku" className="danger" onClick={() => setForm({ ...form, bundleItems: form.bundleItems.filter((_, i) => i !== index) })}>✕</button>
              </div>
              })}
              {bundleComponentOptions.length === 0 && (bundleFilter.search || bundleFilter.category) && <small className="form-warning">Žiadny produkt nevyhovuje hľadaniu/filtru.</small>}
              <button type="button" className="text-btn" onClick={() => setForm({ ...form, bundleItems: [...form.bundleItems, { componentProductId: '', quantity: '1' }] })}>＋ Pridať zložku</button>
              {!form.bundleItems.length && <small className="form-warning">Komplet nemá žiadne zložky – pridaj aspoň jednu.</small>}
            </>}
          </fieldset>
          <fieldset className="allergen-picker">
            <legend>Alergény <small>– zobrazia sa zákazníkom v e-shope</small></legend>
            <div className="allergen-grid">
              {ALLERGENS.map(allergen => <label key={allergen.id} className={`allergen-chip ${allergenState.ids.includes(allergen.id) ? 'is-on' : ''}`}>
                <input type="checkbox" checked={allergenState.ids.includes(allergen.id)} onChange={e => setForm({ ...form, allergens: formatAllergens(e.target.checked ? [...allergenState.ids, allergen.id] : allergenState.ids.filter(id => id !== allergen.id)) })} />
                <b>{allergen.id}</b> {allergen.name}
              </label>)}
            </div>
            <label className={`allergen-chip allergen-none ${allergenState.none ? 'is-on' : ''}`}>
              <input type="checkbox" checked={allergenState.none} onChange={e => setForm({ ...form, allergens: e.target.checked ? formatAllergens([], true) : '' })} /> Neobsahuje žiadne alergény
            </label>
            {allergenState.legacy && <small className="form-warning">Pôvodný zápis „{allergenState.legacy}“ – po zaškrtnutí alergénov sa nahradí.</small>}
            {form.allergens && !allergenState.legacy && <small className="allergen-preview">Zákazník uvidí: {form.allergens}</small>}
          </fieldset>
          <fieldset className="diet-picker">
            <legend>Piktogramy <small>– zobrazia sa pri produkte aj v tlačových zostavách</small></legend>
            <div className="diet-grid">
              <label className={`allergen-chip ${form.glutenFree ? 'is-on' : ''}`}>
                <input type="checkbox" checked={form.glutenFree} onChange={e => setForm({ ...form, glutenFree: e.target.checked })} /> 🌾 Bezlepkové
              </label>
              <label className={`allergen-chip ${form.lactoseFree ? 'is-on' : ''}`}>
                <input type="checkbox" checked={form.lactoseFree} onChange={e => setForm({ ...form, lactoseFree: e.target.checked })} /> 🥛 Bezlaktózové
              </label>
            </div>
          </fieldset>



{editingId && (
  <div className="product-gallery-section">
    <div className="gallery-heading">
      <div>
        <h3>Fotografie produktu</h3>
        <p>Poradie zmeníš potiahnutím – prvá fotografia je hlavná. Nastav, ktoré sa zobrazia zákazníkom.</p>
      </div>
      <span className="gallery-count">{productImages.length} {productImages.length === 1 ? 'fotografia' : 'fotografií'}</span>
    </div>

    <label className="image-upload-zone">
      <span className="image-upload-icon">＋</span>
      <span className="image-upload-copy"><strong>Pridať fotografie</strong><small>JPG, PNG alebo WebP · maximálne 5 MB na fotografiu</small></span>
      <input className="image-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={e => setSelectedFiles(Array.from(e.target.files ?? []))} />
    </label>

    {selectedFiles.length > 0 && (
      <div className="image-upload-actions">
        <span>Vybrané fotografie: <strong>{selectedFiles.length}</strong></span>
        <button type="button" className="primary-btn" disabled={imageLoading} onClick={() => void uploadProductImages()}>
          {imageLoading ? 'Nahrávam…' : 'Nahrať fotografie'}
        </button>
      </div>
    )}

    <div className="product-image-grid">
      {productImages.map(image => (
        <article className={`product-image-card ${image.isMain ? 'is-main' : ''} ${image.isAdminMain ? 'is-admin-main' : ''}`} key={image.id} {...imageDrag.itemProps(image.id)} title="Potiahni a presuň – prvá fotografia je hlavná">
          <div className="product-image-preview">
            <img src={`${API}${image.url}`} alt="Fotografia produktu" />
            {image.isMain && <span className="main-photo-badge">★ Hlavná fotografia</span>}
            {image.isAdminMain && <span className="main-photo-badge admin-photo-badge">🏷 Fotka pre administráciu</span>}
          </div>
          <div className="product-image-details">
            <span className={`visibility-pill ${image.visible ? 'is-visible' : 'is-hidden'}`}>
              <span className="visibility-dot" />{image.visible ? 'Viditeľná v e-shope' : 'Skrytá pred zákazníkmi'}
            </span>
            <div className="product-image-actions">
              {!image.isMain && <button type="button" className="image-action-btn image-main-btn" onClick={() => void updateProductImage(image, { isMain: true })}>Nastaviť ako hlavnú</button>}
              {image.isAdminMain
                ? <button type="button" className="image-action-btn" onClick={() => void updateProductImage(image, { isAdminMain: false })} title="Vrátiť sa k automatickému výberu fotky pre administráciu">Zrušiť pre administráciu</button>
                : <button type="button" className="image-action-btn image-main-btn" onClick={() => void updateProductImage(image, { isAdminMain: true })} title="Táto fotka sa bude zobrazovať v sklade, dokladoch a cenovkách – aj keď je skrytá pred zákazníkmi">Nastaviť pre administráciu</button>}
              <button type="button" className={`image-action-btn ${image.visible ? '' : 'image-publish-btn'}`} onClick={() => void updateProductImage(image, { visible: !image.visible })}>{image.visible ? 'Skryť' : 'Zverejniť'}</button>
              <button type="button" className="image-action-btn image-delete-btn" onClick={() => void deleteProductImage(image)}>Odstrániť</button>
            </div>
          </div>
        </article>
      ))}
      {!productImages.length && <div className="image-empty-state"><span>♡</span><strong>Zatiaľ bez fotografií</strong><small>Pridaj prvé fotografie výrobku.</small></div>}
    </div>
  </div>
)}

<label className="check-label"><input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Produkt je dostupný</label>
<label className="check-label"><input type="checkbox" checked={form.eshopVisible} onChange={e => setForm({ ...form, eshopVisible: e.target.checked })} /> Zobraziť v e-shope (zákazníci si ho môžu objednať)</label>

          {productError && <p className="error-message">{productError}</p>}

          <div className="form-actions">{editingId && <button type="button" className="cancel-btn" onClick={() => void deleteProduct()}>Odstrániť produkt</button>}<button type="button" className="cancel-btn" onClick={resetForm}>Zrušiť</button>

            <button className="primary-btn" disabled={saving}>{saving ? 'Ukladám…' : 'Uložiť produkt'}</button></div>

        </form>

      </div>}

    </div>

  )

}



function OrderTable({ orders, onSelectOrder }: { orders: Order[]; onSelectOrder: (order: Order) => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [withSummary, setWithSummary] = useState(true)
  // výber platí iba pre zobrazené objednávky (po zmene filtra sa zúži)
  const visibleIds = useMemo(() => new Set(orders.map(o => o.id)), [orders])
  const chosen = [...selected].filter(id => visibleIds.has(id))
  if (!orders.length) return null
  const allOn = chosen.length === orders.length
  const toggle = (id: string) => setSelected(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next })
  const printIds = (ids: string[], format: 'a5' | 'a4', label: string) => {
    if (!ids.length) return
    openPrint(`/api/admin/orders-print?${new URLSearchParams({ ids: ids.join(','), format, ...(withSummary && ids.length > 1 ? { summary: '1' } : {}) })}`, `${label} (${ids.length}) – ${format.toUpperCase()}`)
  }
  // „všetky" = všetky zobrazené podľa filtra okrem zrušených a zamietnutých
  const activeIds = orders.filter(o => !['CANCELLED', 'REJECTED'].includes(o.status)).map(o => o.id).slice(0, 300)
  const printChosen = (format: 'a5' | 'a4') => printIds(orders.filter(o => chosen.includes(o.id)).map(o => o.id), format, 'Vybrané objednávky')
  const printAll = (format: 'a5' | 'a4') => printIds(activeIds, format, 'Všetky objednávky')
  return <>
    <div className={`or-batch ${chosen.length ? '' : 'is-all'}`} role="toolbar" aria-label="Dávková tlač">
      {chosen.length > 0
        ? <strong>Vybrané: {chosen.length}</strong>
        : <strong>Zobrazené: {activeIds.length} {activeIds.length === 1 ? 'objednávka' : activeIds.length >= 2 && activeIds.length <= 4 ? 'objednávky' : 'objednávok'}</strong>}
      {(chosen.length || activeIds.length) > 1 && <label className="or-batch-check"><input type="checkbox" checked={withSummary} onChange={e => setWithSummary(e.target.checked)} /> so súhrnom výroby</label>}
      <span className="or-batch-spacer" />
      {chosen.length > 0 ? <>
        <button type="button" className="primary-btn" onClick={() => printChosen('a5')}>🖨 Tlačiť vybrané A5</button>
        <button type="button" className="pa-ghost" onClick={() => printChosen('a4')}>A4</button>
        <button type="button" className="pa-link" onClick={() => setSelected(new Set())}>Zrušiť výber</button>
      </> : <>
        <button type="button" className="primary-btn" disabled={!activeIds.length} onClick={() => printAll('a5')}>🖨 Tlačiť všetky A5</button>
        <button type="button" className="pa-ghost" disabled={!activeIds.length} onClick={() => printAll('a4')}>A4</button>
      </>}
    </div>
    <div className="pa-table-wrap or-table-wrap"><table className="pa-table or-table">
    <thead><tr>
      <th className="or-check"><input type="checkbox" aria-label="Vybrať všetky zobrazené" checked={allOn} ref={el => { if (el) el.indeterminate = chosen.length > 0 && !allOn }} onChange={() => setSelected(allOn ? new Set() : new Set(orders.map(o => o.id)))} /></th>
      <th>Objednávka</th><th>Zákazník</th><th>Termín</th><th>Položky</th><th>Platba</th><th className="pa-col-price">Suma a stav</th><th className="or-actions-col" aria-label="Náhľad" />
    </tr></thead>
    <tbody>{orders.map(order => {
      const term = orderTerm(order)
      const schedule = scheduleStatus(order.scheduleStatus)
      const status = orderStatus(order.status)
      const items = order.items ?? []
      const itemText = items.map(item => `${Number(item.quantity)} × ${item.product.name}${item.variantName ? ` (${item.variantName})` : ''}`)
      const isOn = selected.has(order.id)
      return <tr key={order.id} onClick={() => onSelectOrder(order)} className={`${['CANCELLED', 'REJECTED'].includes(order.status) ? 'is-inactive' : ''} ${isOn ? 'is-selected' : ''}`}>
        <td className="or-check" onClick={e => e.stopPropagation()}><input type="checkbox" aria-label={`Vybrať ${order.orderNumber}`} checked={isOn} onChange={() => toggle(order.id)} /></td>
        <td className="order-number">
          <button type="button" className="order-number-button" onClick={() => onSelectOrder(order)}>{order.orderNumber}</button>{order.seasonalRuleId && <span className="or-season" title="Vianočná (sezónna) objednávka">🎄</span>}
          <small className="or-sub">{order.createdAt ? `prijatá ${friendlyDateTime(order.createdAt)}` : ''}</small>
        </td>
        <td>
          <strong className="or-name">{order.customerName}</strong>
          <small className="or-sub">{order.companyName ? `${order.companyName} · ` : ''}{deliveryShort(order.deliveryMethod, order.store?.name)}{order.deliveryPricePending ? ' · naceniť' : ''}</small>
        </td>
        <td>
          {term ? <strong className="or-term">{friendlyDateTime(term.at)}</strong> : <span className="pa-muted">Bez termínu</span>}
          <span className={`or-pill tone-${schedule.tone}`}>{term?.kind === 'návrh' ? 'Navrhnutý iný termín' : schedule.label}</span>
        </td>
        <td className="or-items" title={itemText.join('\n')}>
          {itemText.slice(0, 2).map(text => <span key={text}>{text}</span>)}
          {itemText.length > 2 && <small className="or-sub">+ {itemText.length - 2} ďalšie</small>}
          {!itemText.length && <span className="pa-muted">—</span>}
        </td>
        <td><span className={`or-pill tone-${paymentTone(order.paymentStatus)}`}>{paymentLabels[order.paymentStatus ?? ''] ?? order.paymentStatus ?? '—'}</span><small className="or-sub">{order.paymentMethod === 'CARD_ONLINE' ? 'Kartou online' : order.paymentMethod === 'CASH_ON_DELIVERY' ? 'Dobierka' : 'Pri odbere'}</small></td>
        <td className="pa-col-price"><strong className="or-amount">{euro(order.totalCents)}</strong><span className={`or-pill is-strong tone-${status.tone}`}>{status.label}</span></td>
        <td className="or-actions-col" onClick={e => e.stopPropagation()}><button type="button" className="or-preview-btn" title="Náhľad tlače" onClick={() => openPrint(`/api/admin/orders-print?ids=${encodeURIComponent(order.id)}&format=a5`, `Objednávka ${order.orderNumber}`)}>👁 Náhľad</button></td>
      </tr>
    })}</tbody>
  </table></div>
  </>
}
export default App
